import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Select from '@radix-ui/react-select';
import * as Tabs from '@radix-ui/react-tabs';
import * as Dialog from '@radix-ui/react-dialog';
import { BsGear, BsChevronDown, BsHeartFill, BsDiamondFill, BsSuitClubFill, BsSuitSpadeFill, BsX, BsClock, BsSearch, BsSend, BsStickyFill, BsBackspace, BsArrowReturnRight, BsArrowUp, BsThreeDots, BsFloppyFill } from 'react-icons/bs';
import { PlayFab } from './services/PlayFab';
import './HandNotesPage.css';
import { Action } from '../components/ActionTimeline/ActionTimeline';
import HandNotesTable from './components/HandNotesTable';
import { HandHistoryCard } from './components/HandHistoryCard';
import ActionBar from './components/ActionBar';

import { evaluateTexasHoldemHand } from '../utils/handStrengthEvaluator';
import { Account } from './services/Account';
import RaiseAmountPopup from '../components/RaiseAmountPopup/RaiseAmountPopup';
import StackSizePopup from '../components/RaiseAmountPopup/StackSizePopup';
import { 
    NewHandSummaryJson,
    HandSummaryMetadata,
    HandSummaryPlayer,
    HandSummaryHeroCards,
    HandSummaryStreet,
    HandSummaryActionWithPlayerId,
    Position,
    ActionType 
} from './BuddyPage';

// -----------------------------------------------------------------------------
// Utility to build a lightweight summary JSON (with hero hand rank per street)
// -----------------------------------------------------------------------------
interface HeroHandRank {
  name: string;
  value: number;
  description: string;
}

const splitCards = (cards: string): string[] =>
  cards ? cards.match(/.{1,2}/g) || [] : [];

const buildSummaryJson = (note: HandNote, players: Player[], finalPot: number) => {
  const holeCardsArr = splitCards(note.heroCards);
  const flopArr     = splitCards(note.flopCards);
  const turnArr     = note.turnCard ? [note.turnCard] : [];
  const riverArr    = note.riverCard ? [note.riverCard] : [];

  const toRank = (e: ReturnType<typeof evaluateTexasHoldemHand>): HeroHandRank => ({
    name: e.rank,
    value: e.rankValue,
    description: e.description,
  });

  const pre   = evaluateTexasHoldemHand(holeCardsArr, []);
  const flop  = evaluateTexasHoldemHand(holeCardsArr, flopArr);
  const turn  = evaluateTexasHoldemHand(holeCardsArr, [...flopArr, ...turnArr]);
  const river = evaluateTexasHoldemHand(holeCardsArr, [...flopArr, ...turnArr, ...riverArr]);

  // Map players to summary-compatible structure
  const summaryPlayers = players.map(p => ({
    id: p.id,
    name: p.position, // Using position as placeholder name
    isHero: p.position === note.position,
    position: p.position as any,
    initialStack: p.stackSize || 0,
  }));

  // Build per-street action arrays & compute pot sizes
  type StreetKey = 'preflop' | 'flop' | 'turn' | 'river';
  const streetKeys: StreetKey[] = ['preflop','flop','turn','river'];
  const streetObj: Record<StreetKey, any> = {
    preflop: { potSizeAtStart: 0, potSizeAtEnd: 0, actions: [] },
    flop:    { potSizeAtStart: 0, potSizeAtEnd: 0, actions: [] },
    turn:    { potSizeAtStart: 0, potSizeAtEnd: 0, actions: [] },
    river:   { potSizeAtStart: 0, potSizeAtEnd: 0, actions: [] },
  };

  // helper to add an action entry
  const pushAction = (street: StreetKey, playerPos: string, type: string, amt?: number) => {
    const playerId = players.find(p=>p.position===playerPos)?.id ?? 0;
    streetObj[street].actions.push({playerId,type,amount:amt});
  };

  // Translate existing ActionTimeline-style actions
  (note.actions || []).forEach((a) => {
    const street = a.street as StreetKey;
    if (!streetObj[street]) return;
    pushAction(street, a.playerName, a.action, a.amount);
  });

  // --- Compute pot sizes ---
  let runningPot = 0;
  const sb = parseFloat(note.smallBlind)||0;
  const bb = parseFloat(note.bigBlind)||0;
  // blinds always in pot preflop
  runningPot += sb + bb;
  streetObj.preflop.potSizeAtStart = sb + bb;

  streetKeys.forEach((street, idx)=>{
    const actions = streetObj[street].actions as any[];
    actions.forEach(ac=>{
      if (ac.amount && ac.amount>0) runningPot += ac.amount;
    });
    streetObj[street].potSizeAtEnd = runningPot;
    // next street start pot
    const nextStreet = streetKeys[idx+1];
    if (nextStreet) streetObj[nextStreet].potSizeAtStart = runningPot;
  });

  // Attach hero hand rank info
  streetObj.preflop.heroHandRank = toRank(pre);
  streetObj.flop.heroHandRank    = toRank(flop);
  streetObj.turn.heroHandRank    = toRank(turn);
  streetObj.river.heroHandRank   = toRank(river);

  // Metadata
  const metadata = {
    handId: note.handId || '',
    timestamp: new Date().toISOString(),
    handType: 'Live',
    tableType: 'Cash Game',
    stakes: { smallBlind: parseFloat(note.smallBlind) || 0, bigBlind: parseFloat(note.bigBlind) || 0 },
  } as HandSummaryMetadata;

  const extras: any = {};
  if (note.showdownCardsByPlayer) extras.showdownCardsByPlayer = note.showdownCardsByPlayer;
  if (note.showdownMuckByPlayer) extras.showdownMuckByPlayer = note.showdownMuckByPlayer;
  if (note.showdownWinners) extras.showdownWinners = note.showdownWinners;

  // Simple pot distribution if winners are marked
  if (Array.isArray(note.showdownWinners) && note.showdownWinners.length > 0 && finalPot > 0) {
    const winners = note.showdownWinners; // positions lowercased
    const payoutPerWinner = finalPot / winners.length;
    const heroKey = (note.position||'').toLowerCase();
    const heroWon = winners.includes(heroKey as any) || winners.includes((players.find(p=>p.position.toLowerCase()===heroKey)?.id as any));
    const bb = parseFloat(note.bigBlind) || 0;
    extras.results = {
      winners,
      payoutPerWinner,
      heroWon,
      heroPayout: heroWon ? payoutPerWinner : 0,
      heroPayoutBB: heroWon && bb > 0 ? payoutPerWinner / bb : undefined,
    };
  }

  return {
    metadata,
    players: summaryPlayers,
    heroCards: holeCardsArr.length===2 ? { holeCard1: holeCardsArr[0], holeCard2: holeCardsArr[1] } : undefined,
    board: [...flopArr, ...turnArr, ...riverArr],
    handContext: {
      heroCards: holeCardsArr.length===2 ? { holeCard1: holeCardsArr[0], holeCard2: holeCardsArr[1] } : null,
      board: [...flopArr, ...turnArr, ...riverArr],
      currentStreet: (riverArr.length ? 'river' : (turnArr.length ? 'turn' : (flopArr.length ? 'flop' : 'preflop'))),
      perStreet: {
        preflop: streetObj.preflop?.heroHandRank || null,
        flop: streetObj.flop?.heroHandRank || null,
        turn: streetObj.turn?.heroHandRank || null,
        river: streetObj.river?.heroHandRank || null,
      }
    },
    preflop: streetObj.preflop,
    flop: streetObj.flop,
    turn: streetObj.turn,
    river: streetObj.river,
    finalPot: finalPot,
    ...extras,
  } as NewHandSummaryJson & typeof extras;
};


interface HandNotesPageProps {}

interface HandNote {
    sessionId: string;
    smallBlind: string;
    bigBlind: string;
    numberOfPlayers: string;
    position: string;
    heroCards: string;
    heroPreflopNotes: string;
    heroFlopNotes: string;
    heroTurnNotes: string;
    heroRiverNotes: string;
    heroShowdownNotes: string;
    preflopNotes: string;
  flopCards: string; // Format: "AhKdQs" (3 cards)
  flopNotes: string;
  turnCard: string; // Format: "Jc" (1 card)
  turnNotes: string;
  riverCard: string; // Format: "2d" (1 card)
  riverNotes: string;
  showdownNotes: string;
  handId?: string;
  actions?: Action[];
  // Showdown extras
  showdownCardsByPlayer?: Record<number, string>;
  showdownMuckByPlayer?: Record<number, boolean>;
  showdownWinners?: string[]; // store by position key (lowercase), supports consistent mapping
}

interface Player {
  id: number;
  position: string;
  isActive: boolean;
  stackSize: number; // in big blinds
}

interface SessionSettings {
  sessionId: string;
  smallBlind: string;
  bigBlind: string;
  numberOfPlayers: string;
}

type StreetTab = 'preflop' | 'flop' | 'turn' | 'river' | 'showdown';

// Shared type for all card picker positions
type CardPosition = string; // allow unique positions per player on showdown (e.g., sd-sb-card1)

// Define the STORAGE_KEY for localStorage
const STORAGE_KEY = 'pokerBuddy_handNotes_sessionSettings';
const LAST_HAND_KEY = 'pokerBuddy_last_hand';

// Define all positions and availability by table size
// Order for cycling seats after each submit (clockwise button move)
const ALL_POSITIONS = [
  { value: 'bb',  label: 'BB',  minPlayers: 2 },  // will become SB next hand
  { value: 'sb',  label: 'SB',  minPlayers: 2 },  // then BTN
  { value: 'btn', label: 'BTN', minPlayers: 3 },  // then CO
  { value: 'co',  label: 'CO',  minPlayers: 4 },
  { value: 'hj',  label: 'HJ',  minPlayers: 7 },
  { value: 'mp2', label: 'MP+2', minPlayers: 9 },
  { value: 'mp1', label: 'MP+1', minPlayers: 8 },
  { value: 'mp',  label: 'MP',  minPlayers: 6 },
  { value: 'utg1',label: 'UTG+1',minPlayers: 7 },
  { value: 'utg', label: 'UTG', minPlayers: 4 },
];

const playerCounts = [4, 5, 6, 7, 8, 9, 10].map(count => ({
  value: String(count),
  label: `${count}-MAX`
}));

// Order of positions for determining action order preflop (first to act is earliest in this list)
const PREFLOP_ORDER: string[] = [
  'utg',
  'utg1',
  'mp',
  'mp1',
  'mp2',
  'hj',
  'co',
  'btn',
  'sb',
  'bb',
];

// Clockwise table order starting from BTN and moving left
const CLOCKWISE_ORDER: string[] = [
  'btn',
  'sb',
  'bb',
  'utg',
  'utg1',
  'mp',
  'mp1',
  'mp2',
  'hj',
  'co',
];

const sortPlayersPreflop = (players: Player[]): Player[] => {
  return [...players].sort((a, b) => PREFLOP_ORDER.indexOf(a.position as Position) - PREFLOP_ORDER.indexOf(b.position as Position));
};

// Determine the first player to act pre-flop (heads-up = SB, otherwise first active in PREFLOP_ORDER)
const getFirstToActPreflop = (players: Player[]): number | null => {
  if (players.length <= 2) {
    const sb = players.find(p => p.position === 'sb');
    return sb ? sb.id : players[0]?.id ?? null;
  }
  const ordered = sortPlayersPreflop(players);
  const first = ordered.find(p => p.isActive);
  return first ? first.id : null;
};

const sortPlayersClockwise = (players: Player[]): Player[] => {
  return [...players].sort((a, b) => CLOCKWISE_ORDER.indexOf(a.position as string) - CLOCKWISE_ORDER.indexOf(b.position as string));
};

const getFirstToActPostflop = (players: Player[]): number | null => {
  if (players.length === 0) return null;

  // Heads-up: player who is NOT button acts first post-flop (button is SB)
  if (players.length === 2) {
    const btn = players.find(p => p.position === 'btn');
    const first = players.find(p => p.id !== btn?.id && p.isActive);
    return first ? first.id : null;
  }

  const ordered = sortPlayersClockwise(players);
  const btnIndex = ordered.findIndex(p => p.position === 'btn');
  if (btnIndex === -1) {
    // Fallback: start at SB
    const sb = ordered.find(p => p.position === 'sb' && p.isActive);
    if (sb) return sb.id;
    const firstActive = ordered.find(p => p.isActive);
    return firstActive ? firstActive.id : null;
  }

  const total = ordered.length;
  for (let i = 1; i < total; i++) {
    const idx = (btnIndex + i) % total;
    const candidate = ordered[idx];
    if (candidate.isActive) return candidate.id;
  }
  return null;
};

// -----------------------------------------------------------------------------
// Helper: Determine if the betting round on a street is complete
// -----------------------------------------------------------------------------
const isBettingRoundComplete = (
  street: StreetTab,
  actions: Action[] = [],
  players: Player[]
): boolean => {
  // Only players who have not folded
  const activeSeats = players.filter((p) => p.isActive);
  if (players.length === 0) return false; // players not initialised yet
  if (activeSeats.length <= 1) return true; // Hand is effectively over

  // Aggregate each seat's contribution on this street
    const normalize = (v: string) => v.toLowerCase();

  const contributions: Record<number, number> = {};
  actions
    .filter(
      (a) =>
        a.street === street &&
        a.amount !== undefined &&
        a.amount !== null &&
        a.amount! > 0
    )
    .forEach((a) => {
      const seatId = players.find((p) => normalize(p.position) === normalize(a.playerName))?.id;
      if (seatId !== undefined) {
        contributions[seatId] = (contributions[seatId] || 0) + (a.amount as number);
      }
    });

  const liveBet = Object.values(contributions).length
    ? Math.max(...Object.values(contributions))
    : 0;

  // Ensure every active seat has acted at least once on this street
    const everyoneActed = activeSeats.every((seat) =>
    actions.some((a) => a.street === street && normalize(a.playerName) === normalize(seat.position))
  );
  if (!everyoneActed) return false;

  if (liveBet === 0) {
    // Check-through round: everyone has acted and put in 0 chips
    return true;
  }

  // Otherwise require each active seat to have matched the live bet
  return activeSeats.every((seat) => (contributions[seat.id] || 0) === liveBet);
};

// Define card ranks and suits
const CARD_RANKS = ['A', 'K', 'Q', 'J', 'T', '9', '8', '7', '6', '5', '4', '3', '2'];

// Optimize ranks for a 4-column grid layout with "2" at the bottom
const DISPLAY_RANKS = CARD_RANKS;

const CARD_SUITS = [
  { symbol: 'h', label: 'Hearts', icon: <BsHeartFill className="heart-icon" /> },
  { symbol: 'd', label: 'Diamonds', icon: <BsDiamondFill className="diamond-icon" /> },
  { symbol: 'c', label: 'Clubs', icon: <BsSuitClubFill className="club-icon" /> },
  { symbol: 's', label: 'Spades', icon: <BsSuitSpadeFill className="spade-icon" /> }
];

// Normalize various hero card shapes (string, array, object) into a compact string like "AhKd"
const normalizeCardsString = (value: any): string => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  // Array form like ["Ah","Kd"]
  if (Array.isArray(value)) {
    const [c1, c2] = value;
    return (typeof c1 === 'string' && typeof c2 === 'string') ? `${c1}${c2}` : '';
  }
  // Object form like { holeCard1: 'Ah', holeCard2: 'Kd' } or { card1: 'Ah', card2: 'Kd' }
  if (typeof value === 'object') {
    const c1 = value.holeCard1 || value.card1 || value[0];
    const c2 = value.holeCard2 || value.card2 || value[1];
    if (typeof c1 === 'string' && typeof c2 === 'string') return `${c1}${c2}`;
  }
  return '';
};

// Function to generate a short, human-friendly 5-letter handId (e.g., YKLND)
const generateHandId = (): string => {
  const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  try {
    // Prefer cryptographic randomness when available
    const arr = new Uint32Array(5);
    if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
      window.crypto.getRandomValues(arr);
      return Array.from(arr, n => ALPHABET[n % 26]).join('');
    }
  } catch {}
  // Fallback to Math.random
  return Array.from({ length: 5 }, () => ALPHABET[Math.floor(Math.random() * 26)]).join('');
};

// Load session settings from localStorage
const loadSessionSettings = (): SessionSettings => {
  const savedSettings = localStorage.getItem(STORAGE_KEY);
  console.log("Loading settings from localStorage:", savedSettings);
  
  if (savedSettings) {
    try {
      const parsedSettings = JSON.parse(savedSettings);
      console.log("Successfully parsed settings:", parsedSettings);
      return parsedSettings;
    } catch (error) {
      console.error('Error loading session settings:', error);
    }
  }
  
  console.log("Using default settings");
  // Default settings
  return {
    sessionId: '',
    smallBlind: '',
    bigBlind: '',
    numberOfPlayers: '9'
  };
};

// Save session settings to localStorage
const saveSessionSettings = (settings: SessionSettings): void => {
  console.log("Saving settings to localStorage:", settings);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
};

// --- NEW HandHistory Interface (based on NewHandSummaryJson) ---
export interface HandHistory extends NewHandSummaryJson {}
// --- END NEW HandHistory Interface ---

interface NotesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: string;
  onChange: (value: string) => void;
  label: string;
}

const NotesDialog: React.FC<NotesDialogProps> = ({ open, onOpenChange, value, onChange, label }) => (
  <Dialog.Root open={open} onOpenChange={onOpenChange}>
    <Dialog.Portal>
      <Dialog.Overlay className="card-dialog-overlay" />
      <Dialog.Content className="card-dialog-content">
        <Dialog.Title className="card-dialog-title">{label}</Dialog.Title>
        <textarea
          style={{ width: '100%', minHeight: 120, margin: '10px 0' }}
          value={value}
          onChange={e => onChange(e.target.value)}
          autoFocus
        />
        <Dialog.Close asChild>
          <button className="card-dialog-close" aria-label="Close">
            <BsX />
          </button>
        </Dialog.Close>
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>
);

const HandNotesPage: React.FC<HandNotesPageProps> = () => {
  // Load saved settings from localStorage on component mount
  const savedSettings = loadSessionSettings();
  const navigate = useNavigate();
  
  // Create refs for each text area input
  const preflopNotesRef = useRef<HTMLTextAreaElement>(null);
  const flopNotesRef = useRef<HTMLTextAreaElement>(null);
  const turnNotesRef = useRef<HTMLTextAreaElement>(null);
  const riverNotesRef = useRef<HTMLTextAreaElement>(null);
  const showdownNotesRef = useRef<HTMLTextAreaElement>(null);
  
  const [handNote, setHandNote] = useState<HandNote>({
    sessionId: savedSettings.sessionId,
    smallBlind: savedSettings.smallBlind,
    bigBlind: savedSettings.bigBlind,
    numberOfPlayers: savedSettings.numberOfPlayers,
    position: 'btn',
    heroCards: '',
    heroPreflopNotes: '',
    heroFlopNotes: '',
    heroTurnNotes: '',
    heroRiverNotes: '',
    heroShowdownNotes: '',
    preflopNotes: '',
    flopCards: '',
    flopNotes: '',
    turnCard: '',
    turnNotes: '',
    riverCard: '',
    riverNotes: '',
    showdownNotes: '',
    handId: generateHandId(),
    actions: [],
  });

  const [activeTab, setActiveTab] = useState<StreetTab>('preflop');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [templateText, setTemplateText] = useState('');
  const [isViewTemplateOpen, setIsViewTemplateOpen] = useState(false);
  const [viewTemplateText, setViewTemplateText] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [historySessionId, setHistorySessionId] = useState('');
  const [handNotes, setHandNotes] = useState<any[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  
  // NEW: History modal tabs and analyzed results state
  type HistoryTab = 'drafts' | 'results';
  const [historyTab, setHistoryTab] = useState<HistoryTab>('drafts');
  
  interface SessionHandSummary {
    handId: string;
    timestamp: string;
    summaryJson?: any;
    potSize?: number;
    overallRating?: number;
    heroProfitBB?: number;
    handType?: string;
  }
  const [resultsHands, setResultsHands] = useState<SessionHandSummary[]>([]);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [resultsError, setResultsError] = useState<string | null>(null);
  
  // NEW: Sessions list for History modal (so user can pick without typing)
  const [sessions, setSessions] = useState<string[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [sessionsError, setSessionsError] = useState<string | null>(null);
  const [selectedHandNote, setSelectedHandNote] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  
  const [players, setPlayers] = useState<Player[]>([]);
  const [activePlayerId, setActivePlayerId] = useState<number | null>(null);
  const [lastActions, setLastActions] = useState<Record<number, Array<{ action: string; amount?: number; number: number }>>>({});
  const [isRaisePopupOpen, setIsRaisePopupOpen] = useState(false);
  const [isStackPopupOpen, setIsStackPopupOpen] = useState(false);
  const [currentBet, setCurrentBet] = useState(0); // State to track current bet amount to call
  const skipAutoInitPlayersRef = useRef(false);

  // ADDED: State for pot size
  const [potSize, setPotSize] = useState(0);

  // ADDED: State for hand history conversion
  const [convertedHandHistory, setConvertedHandHistory] = useState<HandHistory | null>(null);
  const [isConverting, setIsConverting] = useState(false);
  const [conversionError, setConversionError] = useState<string | null>(null);
  const [overrideSummaryJson, setOverrideSummaryJson] = useState<any | null>(null);
  // END ADDED: State for hand history conversion

  // --- Range lookup helper (PokerHands table via CloudScript -> DynamoDB Lambda) ---
  const mapUiPosToRangePos = (p: string): string => {
    const key = (p || '').toLowerCase();
    const m: Record<string,string> = {
      bb: 'BB', sb: 'SB', btn: 'BTN', co: 'CO', hj: 'HJ', mp2: 'MP2', mp1: 'MP1', mp: 'MP', utg1: 'UTG+1', utg: 'UTG'
    };
    return m[key] || (p || '').toUpperCase();
  };

  // Canonicalize flexible hand inputs like "55", "AhQh", "ah qh", "AQs", "AQo"
  const canonicalizeAnyPreflopHand = (raw: string): string | null => {
    if (!raw) return null;
    const s = String(raw).replace(/[^a-z0-9]/gi, '').toUpperCase();
    const RANKS = ['A','K','Q','J','T','9','8','7','6','5','4','3','2'];
    const POS: Record<string, number> = Object.fromEntries(RANKS.map((r,i)=>[r,i]));
    const isRank = (c:string)=> !!POS[c];
    const isSuit = (c:string)=> /[CDHS]/.test(c);

    // Accept pure notation already canonical: AA, AKS, AQO
    if (s.length===2 && isRank(s[0]) && s[0]===s[1]) return s; // pair w/o suits
    if (s.length===3 && isRank(s[0]) && isRank(s[1]) && (s[2]==='S'||s[2]==='O')){
      const a=s[0], b=s[1], flag=s[2].toLowerCase();
      if (a===b) return a+a; // edge case "AAS" -> treat as pair
      const [hi,lo] = POS[a] <= POS[b] ? [a,b] : [b,a];
      return `${hi}${lo}${flag}`;
    }

    // Try parsing as two cards (r1 s1 r2 s2) possibly no suits
    const tryFour = s.match(/^([AKQJT2-9])([CDHS])?([AKQJT2-9])([CDHS])?$/);
    if (tryFour){
      const a=tryFour[1], aSuit=tryFour[2]||''; const b=tryFour[3], bSuit=tryFour[4]||'';
      if (a===b) return `${a}${b}`; // pair
      const suited = !!aSuit && !!bSuit && aSuit===bSuit;
      const [hi,lo] = POS[a] <= POS[b] ? [a,b] : [b,a];
      return `${hi}${lo}${suited?'s':'o'}`;
    }

    // Fallback: attempt to split spaced input like "ah qh"
    const spaced = String(raw).trim().split(/\s+/).map(t=>t.trim().toUpperCase());
    if (spaced.length===2 && spaced[0].length>=1 && spaced[1].length>=1){
      const n1 = spaced[0].replace(/[^AKQJT2-9CDHS]/g,'');
      const n2 = spaced[1].replace(/[^AKQJT2-9CDHS]/g,'');
      return canonicalizeAnyPreflopHand(n1+n2);
    }
    return null;
  };

  const fetchRangeForPosition = async (uiPosition: string) => {
    try{
      const position = mapUiPosToRangePos(uiPosition);
      const format = '9MAX';
      const stack = '200bb';
      const source = 'solver';
      const version = 'v1';
      const result = await PlayFab.executeCloudScript('getDynamoDBData', {
        action: 'list_range', format, stack, position, source, version
      });
      const nestedRoot = (result as any)?.data?.FunctionResult;
      const nested = nestedRoot?.FunctionResult ?? nestedRoot;
      let parsed: any = null;
      if (nested && typeof nested?.body === 'string'){
        try { parsed = JSON.parse(nested.body); } catch {}
      }
      if (!parsed && typeof nested === 'object') parsed = nested;
      if (parsed && parsed.ok && Array.isArray(parsed.range)){
        return parsed; // { ok, pk, position, count, range: [{hand,call,raise,fold}] }
      }
      return null;
    }catch(err){
      console.warn('Range fetch failed (non-fatal):', err);
      return null;
    }
  };

  type NotesModalType = StreetTab | 'hero';
  const [notesModal, setNotesModal] = useState<null | NotesModalType>(null);
  const [handEval, setHandEval] = useState<string>('');

  // Timeline modal state
  const [showTimeline, setShowTimeline] = useState(false);

  // Showdown helpers: opponent cards, mucked flags, winners set
  const [showdownCardsByPlayer, setShowdownCardsByPlayer] = useState<Record<string, string>>({});
  const [showdownMuckByPlayer, setShowdownMuckByPlayer] = useState<Record<string, boolean>>({});
  const [showdownWinners, setShowdownWinners] = useState<Set<string>>(new Set());

  // Keep hero's showdown cards in sync with top-menu hero cards
  const heroKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const key = (handNote.position || '').toLowerCase();
    if (!key) return;
    const heroCards = handNote.heroCards || '';

    // If hero position changed, clear old hero mapping to avoid stale entry
    if (heroKeyRef.current && heroKeyRef.current !== key) {
      const oldKey = heroKeyRef.current;
      setShowdownCardsByPlayer(prev => {
        const copy = { ...prev };
        delete copy[oldKey];
        return copy;
      });
      setShowdownMuckByPlayer(prev => {
        const copy = { ...prev };
        delete copy[oldKey];
        return copy;
      });
      setShowdownWinners(prev => {
        const next = new Set(prev);
        next.delete(oldKey);
        return next;
      });
    }
    heroKeyRef.current = key;

    // Sync showdown cards to hero cards when set
    if (heroCards.length >= 4) {
      const synced = heroCards.substring(0, 4);
      setShowdownCardsByPlayer(prev => ({ ...prev, [key]: synced }));
    }
  }, [handNote.heroCards, handNote.position]);

  // Straddle helpers
  const [straddleCount, setStraddleCount] = useState(0); // Track straddles

  // Shared Card-picker dialog state
  const [activePickerPos, setActivePickerPos] = useState<CardPosition | null>(null);

  // ====== NEW: determine whether the active player can check or must call ======
  const callCheckLabel = (() => {
    const activePlayer = players.find(p => p.id === activePlayerId);
    const bbAmt = parseFloat(handNote.bigBlind) || 0;
    const effectiveBet = activeTab === 'preflop' ? (currentBet > 0 ? currentBet : bbAmt) : currentBet;
    if (!activePlayer) {
      return effectiveBet > 0 ? 'Call' : 'Check';
    }
    const playerContribution = (lastActions[activePlayer.id] || []).reduce((sum, act) => sum + (act.amount || 0), 0);
    const toCall = effectiveBet - playerContribution;
    return toCall > 0 ? 'Call' : 'Check';
  })();
  // ====== END NEW ======

  // Save settings whenever they change
  useEffect(() => {
    const settingsToSave: SessionSettings = {
      sessionId: handNote.sessionId,
      smallBlind: handNote.smallBlind,
      bigBlind: handNote.bigBlind,
      numberOfPlayers: handNote.numberOfPlayers
    };
    
    saveSessionSettings(settingsToSave);
  }, [handNote.sessionId, handNote.smallBlind, handNote.bigBlind, handNote.numberOfPlayers]);

  // ADDED: Effect to calculate pot size whenever actions or blinds change
  useEffect(() => {
    const sb = parseFloat(handNote.smallBlind) || 0;
    const bb = parseFloat(handNote.bigBlind) || 0;
    
    // All actions, including blinds, contribute to the pot
    const actionsTotal = handNote.actions?.reduce((total, action) => total + (action.amount || 0), 0) || 0;
    
    setPotSize(actionsTotal);
  }, [handNote.actions, handNote.smallBlind, handNote.bigBlind]);

  // Ensure settings are loaded on component mount
  useEffect(() => {
    console.log("Component mounted - checking localStorage");
    const savedSettings = loadSessionSettings();
    console.log("Initial load complete, saved settings:", savedSettings);
    
    // Only update if values are different to avoid unnecessary re-renders
    if (savedSettings.sessionId !== handNote.sessionId ||
        savedSettings.smallBlind !== handNote.smallBlind ||
        savedSettings.bigBlind !== handNote.bigBlind ||
        savedSettings.numberOfPlayers !== handNote.numberOfPlayers) {
      
      console.log("Updating handNote with saved settings");
      setHandNote(prevState => ({
        ...prevState,
        sessionId: savedSettings.sessionId,
        smallBlind: savedSettings.smallBlind,
        bigBlind: savedSettings.bigBlind,
        numberOfPlayers: savedSettings.numberOfPlayers
      }));
    }
  }, []); // Empty dependency array means this runs once on mount

  // ADDED: Effect to automatically post blinds for a new hand
  useEffect(() => {
    // Skip when we're hydrating from a draft/summary
    if (skipAutoInitPlayersRef.current) return;
    const sb = parseFloat(handNote.smallBlind);
    const bb = parseFloat(handNote.bigBlind);

    // This should only run for a new hand where actions haven't been set yet.
    if (handNote.actions?.length === 0 && players.length > 0 && !isNaN(sb) && sb > 0 && !isNaN(bb) && bb > 0) {
      const blindActions: Action[] = [
        { playerName: 'SB', action: 'posts', amount: sb, street: 'preflop' },
        { playerName: 'BB', action: 'posts', amount: bb, street: 'preflop' },
      ];

      // deduct blinds from stacks
      setPlayers(prev=>prev.map(p=>{
        if(p.position.toLowerCase()==='sb') return {...p, stackSize: Math.max(p.stackSize - sb,0)};
        if(p.position.toLowerCase()==='bb') return {...p, stackSize: Math.max(p.stackSize - bb,0)};
        return p;
      }));
      
      setHandNote(prev => ({
        ...prev,
        actions: blindActions,
      }));

      // Initialise current bet to the big blind so UTG must call/raise
      setCurrentBet(bb);
    }
  }, [handNote.handId, handNote.smallBlind, handNote.bigBlind, players]);
  
  // ADDED: Effect to display blind badges on the table for a new hand
  useEffect(() => {
    const isNewHandPreflop = activeTab === 'preflop' && handNote.actions?.every(a => a.action === 'posts');
    if (isNewHandPreflop) {
        const sb = parseFloat(handNote.smallBlind);
        const bb = parseFloat(handNote.bigBlind);

        if (isNaN(sb) || isNaN(bb)) return;

        const sbPlayer = players.find(p => p.position.toLowerCase() === 'sb');
        const bbPlayer = players.find(p => p.position.toLowerCase() === 'bb');

        const newLastActions: Record<number, { action: string; amount?: number; number: number }[]> = {};

        if (sbPlayer) {
            newLastActions[sbPlayer.id] = [{ action: 'SB', amount: sb, number: 1 }];
        }
        if (bbPlayer) {
            newLastActions[bbPlayer.id] = [{ action: 'BB', amount: bb, number: 1 }];
        }
        setLastActions(newLastActions);
    }
  }, [handNote.actions, activeTab, players]);

  // ====== NEW: keep currentBet in sync with highest contribution on this street ======
  useEffect(() => {
    const highest = (handNote.actions || [])
      .filter(a => a.street === activeTab && typeof a.amount === 'number')
      .reduce((max, a) => (a.amount! > max ? a.amount! : max), 0);
    setCurrentBet(highest);
  }, [handNote.actions, activeTab, players]);
  // ====== END NEW ======

  // ====== AUTO STREET ADVANCE ======
  const hasAdvancedRef = useRef(false);
  const manualTabGraceUntilRef = useRef<number>(0);

  useEffect(() => {
    if (players.length === 0) return; // wait until seats initialised
    if (Date.now() < manualTabGraceUntilRef.current) return; // respect manual tab change briefly

    // --- Prevent auto-advance when user is viewing an earlier street ---
    const STREET_ORDER: StreetTab[] = ['preflop', 'flop', 'turn', 'river', 'showdown'];
    const latestStreetIdx = Math.max(
      -1,
      ...(handNote.actions || []).map((a) => STREET_ORDER.indexOf(a.street as StreetTab))
    );
    const currentIdx = STREET_ORDER.indexOf(activeTab);
    if (currentIdx < latestStreetIdx) {
      // User is looking back at an earlier street – do NOT auto-advance
      hasAdvancedRef.current = false;
      return;
    }

    if (isBettingRoundComplete(activeTab, handNote.actions || [], players)) {
      if (hasAdvancedRef.current) return; // already advanced for this round
      hasAdvancedRef.current = true;

      const STREET_ORDER: StreetTab[] = ['preflop', 'flop', 'turn', 'river', 'showdown'];
      const nextIdx = STREET_ORDER.indexOf(activeTab) + 1;
      if (nextIdx < STREET_ORDER.length) {
        const nextStreet = STREET_ORDER[nextIdx] as StreetTab;
        setActiveTab(nextStreet);

        // pick first-to-act for the new street
        const nextFirst =
          nextStreet === 'preflop'
            ? getFirstToActPreflop(players)
            : getFirstToActPostflop(players);
        if (nextFirst !== null) setActivePlayerId(nextFirst);

        // reset betting state for new street
        setCurrentBet(0);
        setLastActions({});
      }
    } else {
      hasAdvancedRef.current = false; // keep watching until round completes
    }
  }, [handNote.actions, players, activeTab]);
  // ====== END AUTO STREET ADVANCE ======

  useEffect(() => {
    if (skipAutoInitPlayersRef.current) {
      // Skip one cycle when loading from a draft, then re-enable auto init
      skipAutoInitPlayersRef.current = false;
      return;
    }
    const numPlayers = parseInt(handNote.numberOfPlayers, 10) || 9;
    const availablePositions = getAvailablePositions();

    const newPlayersUnordered = availablePositions.map((pos, index) => ({
      id: index,
      position: pos.value,
      isActive: true,
      stackSize: (()=>{
        const bb= parseFloat(handNote.bigBlind)||1;
        const sb= parseFloat(handNote.smallBlind)|| (bb/2);
        let base = bb*100;
        if(pos.value==='sb') return Math.max(base - sb,0);
        if(pos.value==='bb') return Math.max(base - bb,0);
        return base;
      })(),
    }));

    const orderedPlayers = sortPlayersPreflop(newPlayersUnordered);
    setPlayers(orderedPlayers);

    // Set the first player to act preflop
    const firstToActId = getFirstToActPreflop(orderedPlayers);
    if (firstToActId !== null) {
      setActivePlayerId(firstToActId);
    }
  }, [handNote.numberOfPlayers]);

  const handleAction = (type: 'call' | 'raise' | 'fold' | 'straddle', amount?: number) => {
    if (type === 'raise' && amount === undefined) {
      setIsRaisePopupOpen(true);
      return;
    }
    const activePlayer = players.find(p => p.id === activePlayerId);
    if (!activePlayer) return;
    
    const playerName = activePlayer.position;

    let actionAmount = amount;
    let finalActionType: string = type; // NEW: may change 'call' -> 'check'

    // NEW: handle straddle logic
    if (type === 'straddle') {
      const bbAmount = parseFloat(handNote.bigBlind) || 0;
      actionAmount = bbAmount * Math.pow(2, straddleCount + 1); // 2×BB, 4×BB, 8×BB...
      setStraddleCount(prev => prev + 1);
      setCurrentBet(actionAmount);
    }

    if (type === 'call') {
      const playerActionsOnStreet = lastActions[activePlayer.id] || [];
      const playerContribution = playerActionsOnStreet.reduce((sum, act) => sum + (act.amount || 0), 0);
      const bbAmt = parseFloat(handNote.bigBlind) || 0;
      const effectiveBet = activeTab === 'preflop' ? (currentBet > 0 ? currentBet : bbAmt) : currentBet;
      const toCall = effectiveBet - playerContribution;
      actionAmount = toCall > 0 ? toCall : 0;

      // NEW: if nothing to call, treat as CHECK instead of CALL
      if (toCall <= 0) {
        finalActionType = 'check';
      }
    }

    // Determine the action number within this street so far
    const streetIndex = (handNote.actions || []).filter(a => a.street === activeTab).length + 1;

    const newAction: Action = {
      playerName,
      action: finalActionType,
      amount: actionAmount,
      street: activeTab,
    };
    setHandNote(prev => ({
      ...prev,
      actions: [...(prev.actions || []), newAction],
    }));

    if (type === 'raise' && actionAmount !== undefined) {
      setCurrentBet(actionAmount);
    }

    // If fold, mark seat inactive
    if (type === 'fold' && activePlayerId !== null) {
      setPlayers(prev => prev.map(p => p.id === activePlayerId ? { ...p, isActive: false } : p));
    }

    if (activePlayerId !== null) {
      // Deduct from active player's stackSize
      if(actionAmount !== undefined && actionAmount > 0){
        const amt = actionAmount;
        setPlayers(prev=>prev.map(p=>p.id===activePlayerId?{...p,stackSize:Math.max(p.stackSize - amt,0)}:p));
      }
      // Update last actions map
      setLastActions(prev => {
        const seatActions = Array.isArray(prev[activePlayerId]) ? [...prev[activePlayerId]] : [];
        seatActions.push({ action: finalActionType, amount: actionAmount, number: streetIndex });
        return { ...prev, [activePlayerId]: seatActions };
      });

      // Determine the next player to act (simple clockwise order among active seats)
      const currentIndex = players.findIndex(p => p.id === activePlayerId);
      if (currentIndex !== -1) {
        const total = players.length;
        let nextIndex = (currentIndex + 1) % total;
        let loops = 0;
        let nextPlayerId: number | null = null;

        while (loops < total) {
          const candidate = players[nextIndex];
          if (candidate && candidate.isActive) {
            nextPlayerId = candidate.id;
            break;
          }
          nextIndex = (nextIndex + 1) % total;
          loops += 1;
        }

        if (nextPlayerId !== null) {
          setActivePlayerId(nextPlayerId);
        }
      }
    }
  };

  const handleRaiseSubmit = (amount: number) => {
    handleAction('raise', amount);
    setIsRaisePopupOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setHandNote((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePositionChange = (value: string) => {
    setHandNote((prev) => ({
      ...prev,
      position: value,
    }));
  };

  const handlePlayerCountChange = (value: string) => {
    setHandNote((prev) => ({
      ...prev,
      numberOfPlayers: value,
    }));
  };

  const handleTabChange = (value: string) => {
    // Give user a brief grace period to view the selected street even if complete
    manualTabGraceUntilRef.current = Date.now() + 1200;
    setActiveTab(value as StreetTab);

    // When street changes, rebuild per-seat lastActions for that street
    const newLast: Record<number, { action: string; amount?: number; number: number }[]> = {};
    (handNote.actions || [])
      .filter(a => a.street === value)
      .forEach(a => {
        const seat = players.find(p => p.position.toLowerCase() === (a.playerName||'').toLowerCase());
        if (!seat || !seat.isActive) return; // skip folded players
        // Always keep only the latest action for this seat
        newLast[seat.id] = [{ action: a.action, amount: a.amount, number: 1 }];
      });
    setLastActions(newLast);
    
    // Determine first player to act for new street
    if (value === 'preflop') {
      const firstId = getFirstToActPreflop(players);
      if (firstId !== null) setActivePlayerId(firstId);
    } else if (['flop','turn','river','showdown'].includes(value)) {
      const firstId = getFirstToActPostflop(players);
      if (firstId !== null) setActivePlayerId(firstId);
    }
    
    // Focus the appropriate textarea when tab changes, but prevent auto-scrolling
    setTimeout(() => {
      // Store current scroll position
      const scrollPosition = window.scrollY;
      
      // Focus the appropriate textarea based on tab
      let textareaToFocus: HTMLTextAreaElement | null = null;
      switch(value) {
        case 'preflop':
          textareaToFocus = preflopNotesRef.current;
          break;
        case 'flop':
          textareaToFocus = flopNotesRef.current;
          break;
        case 'turn':
          textareaToFocus = turnNotesRef.current;
          break;
        case 'river':
          textareaToFocus = riverNotesRef.current;
          break;
        case 'showdown':
          textareaToFocus = showdownNotesRef.current;
          break;
      }
      
      // Focus the textarea if available
      if (textareaToFocus) {
        textareaToFocus.focus({preventScroll: true});
        
        // Restore scroll position after focus
        window.scrollTo(0, scrollPosition);
      }
    }, 50);
  };

  const toggleSettings = () => {
    setShowSettings(!showSettings);
  };

  const closeSettings = () => {
    setShowSettings(false);
  };

  const toggleHistory = () => {
    if (!showHistory) {
      setHistorySessionId(handNote.sessionId);
    }
    setShowHistory(!showHistory);
  };

  const closeHistory = () => {
    setShowHistory(false);
  };

  const handleLoadHistory = async (sessionIdOverride?: string) => {
    const sessionId = (sessionIdOverride ?? historySessionId)?.trim();
    if (!sessionId) {
      alert('Please enter a session ID');
      return;
    }
    
    setIsLoadingHistory(true);
    
    try {
      console.log('Fetching hand notes for session:', sessionId);
      const result = await PlayFab.executeCloudScript({
        FunctionName: "getDynamoDBData",
        FunctionParameter: {
          action: "getSessionHandNotes",
          sessionId,
          ownerPlayfabId: PlayFab.getPlayerId()
        }
      });
      console.log('CloudScript getSessionHandNotes raw result:', result);

      if (!result.success) throw new Error('Failed to execute CloudScript');

      const nestedRoot = (result as any)?.data?.FunctionResult;
      const container = nestedRoot?.FunctionResult ?? nestedRoot;
      try {
        console.log('FunctionResult container keys:', container ? Object.keys(container) : 'null');
      } catch {}
      let handNotesData: any[] = [];

      if (Array.isArray(container?.data?.handNotes)) {
        handNotesData = container.data.handNotes;
      } else if (Array.isArray(container?.handNotes)) {
        handNotesData = container.handNotes;
      } else if (container?.data?.body && typeof container.data.body === 'string') {
        try {
          const parsed = JSON.parse(container.data.body);
          if (Array.isArray(parsed?.handNotes)) handNotesData = parsed.handNotes;
        } catch {}
      } else if (container?.body && typeof container.body === 'string') {
        try {
          const parsed = JSON.parse(container.body);
          if (Array.isArray(parsed?.handNotes)) handNotesData = parsed.handNotes;
        } catch {}
      } else if (typeof container?.data === 'string') {
        try {
          const parsed = JSON.parse(container.data);
          if (Array.isArray(parsed?.handNotes)) handNotesData = parsed.handNotes;
        } catch {}
      }

      console.log('Parsed drafts count:', Array.isArray(handNotesData) ? handNotesData.length : 0);
      if (Array.isArray(handNotesData) && handNotesData.length) {
        const sample = handNotesData[0];
        console.log('Sample draft keys:', Object.keys(sample || {}));
        console.log('Sample draft.actions length:', Array.isArray(sample?.actions) ? sample.actions.length : 0);
        if (Array.isArray(sample?.actions)) {
          console.log('Sample first 5 actions:', sample.actions.slice(0,5));
        }
      }

      // Sort newest first by timestamp if present
      const list = Array.isArray(handNotesData) ? handNotesData.slice() : [];
      list.sort((a,b)=>{
        const ta = new Date(a.timestamp || 0).getTime();
        const tb = new Date(b.timestamp || 0).getTime();
        return tb - ta;
      });
      setHandNotes(list);
    } catch (error) {
      console.error('Error loading hand notes:', error);
      alert('Failed to load hand notes. Please try again.');
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // NEW: Load analyzed hands (Results tab)
  const handleLoadResults = async (sessionIdOverride?: string) => {
    const sessionId = (sessionIdOverride ?? historySessionId)?.trim();
    if (!sessionId) {
      alert('Please enter a session ID');
      return;
    }

    setIsLoadingResults(true);
    setResultsError(null);

    try {
      const result = await PlayFab.executeCloudScript('getDynamoDBData', {
        action: 'getSessionHands',
        sessionId: sessionId,
        ownerPlayfabId: PlayFab.getPlayerId()
      });

      const nestedRoot = (result as any)?.data?.FunctionResult;

      let parsed: any[] = [];
      const nested = nestedRoot?.FunctionResult ?? nestedRoot;
      if (Array.isArray(nested?.data)) {
        parsed = nested.data;
      } else if (Array.isArray(nested)) {
        parsed = nested;
      } else if (typeof nested?.data?.body === 'string') {
        try { parsed = JSON.parse(nested.data.body); } catch {}
      } else if (typeof nested?.body === 'string') {
        try { parsed = JSON.parse(nested.body); } catch {}
      }

      setResultsHands(Array.isArray(parsed) ? parsed : []);
      if (!Array.isArray(parsed) || parsed.length === 0) {
        alert('No analyzed hands found for this session');
      }
    } catch (err: any) {
      const msg = err?.message || 'An unknown error occurred while fetching results.';
      setResultsError(msg);
      alert(`Failed to load results: ${msg}`);
    } finally {
      setIsLoadingResults(false);
    }
  };

  // NEW: Load all sessions for current user
  const handleLoadSessions = async () => {
    setIsLoadingSessions(true);
    setSessionsError(null);
    try {
      const result = await PlayFab.executeCloudScript('getDynamoDBData', { action: 'getSessions' });
      const nested = (result as any)?.data?.FunctionResult?.FunctionResult;

      let parsed: any = [];
      if (Array.isArray(nested?.data)) {
        parsed = nested.data;
      } else if (typeof nested?.body === 'string') {
        try { parsed = JSON.parse(nested.body); } catch {}
      } else if (Array.isArray(nested)) {
        parsed = nested;
      }

      const list: string[] = Array.isArray(parsed) ? parsed : [];
      setSessions(list);
    } catch (err: any) {
      const msg = err?.message || 'Failed to load sessions.';
      setSessionsError(msg);
      console.error('Error loading sessions:', err);
    } finally {
      setIsLoadingSessions(false);
    }
  };

  // Load sessions when opening History; also refresh if empty value selected
  useEffect(() => {
    if (showHistory && !isLoadingSessions && sessions.length === 0) {
      handleLoadSessions();
    }
  }, [showHistory]);

  // Auto-load drafts/results when history opens or when tab/session changes
  useEffect(() => {
    if (!showHistory) return;
    const sid = (historySessionId || '').trim();
    if (!sid) return;
    if (historyTab === 'drafts') {
      handleLoadHistory(sid);
    } else if (historyTab === 'results') {
      handleLoadResults(sid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showHistory, historyTab, historySessionId]);

  const handleSelectHandNote = (note: any) => {
    console.log('Selecting draft note:', note?.handId || '(no id)');
    console.log('Draft note keys:', note ? Object.keys(note) : 'null');
    console.log('Draft actions length:', Array.isArray(note?.actions) ? note.actions.length : 0);
    if (Array.isArray(note?.actions)) {
      console.log('Draft first 5 actions:', note.actions.slice(0,5));
    }
    setSelectedHandNote(note);
    setIsEditing(true);
    
    // Convert the DynamoDB hand note to the form format
    setHandNote({
      sessionId: note.sessionId || handNote.sessionId,
      smallBlind: note.smallBlind || handNote.smallBlind,
      bigBlind: note.bigBlind || handNote.bigBlind,
      numberOfPlayers: note.numberOfPlayers || handNote.numberOfPlayers,
      position: note.position || '',
      heroCards: normalizeCardsString(note.heroCards) || '',
      heroPreflopNotes: note.heroPreflopNotes || '',
      heroFlopNotes: note.heroFlopNotes || '',
      heroTurnNotes: note.heroTurnNotes || '',
      heroRiverNotes: note.heroRiverNotes || '',
      heroShowdownNotes: note.heroShowdownNotes || '',
      preflopNotes: note.preflopNotes || '',
      flopCards: note.flopCards || '',
      flopNotes: note.flopNotes || '',
      turnCard: note.turnCard || '',
      turnNotes: note.turnNotes || '',
      riverCard: note.riverCard || '',
      riverNotes: note.riverNotes || '',
      showdownNotes: note.showdownNotes || '',
      handId: note.handId,
      actions: note.actions || [],
    });

    // If draft contains players/pot, hydrate UI state accordingly
    const playersFromDraft: Player[] | null = Array.isArray(note.players) ? note.players : null;
    const actionsFromDraft: Action[] = Array.isArray(note.actions) ? note.actions : [];
    const toLower = (s:string)=> (s||'').toLowerCase();

    // Prepare players list to use immediately for mapping actions
    let mergedPlayers: Player[] | null = null;
    if (playersFromDraft) {
      skipAutoInitPlayersRef.current = true;
      const foldedPositions = new Set(
        actionsFromDraft.filter(a=>toLower(a.action)==='fold').map(a=>toLower(a.playerName))
      );
      mergedPlayers = playersFromDraft.map(p=>({
        ...p,
        isActive: !foldedPositions.has(toLower(p.position)) && p.isActive !== false
      }));
      setPlayers(mergedPlayers);
    }

    // Pot size
    if (typeof note.potSize === 'number') {
      setPotSize(note.potSize);
    }

    // Decide active street to display: latest street present in actions
    const STREET_ORDER: StreetTab[] = ['preflop','flop','turn','river','showdown'];
    const latestStreetIdx = Math.max(-1, ...actionsFromDraft.map(a=>STREET_ORDER.indexOf(a.street as StreetTab)).filter(i=>i>=0));
    if (latestStreetIdx >= 0) {
      const street = STREET_ORDER[latestStreetIdx];
      setActiveTab(street);
      // Build lastActions for that street and current bet using immediate players list if available
      const seatList = mergedPlayers || playersFromDraft || players;
      const map: Record<number, { action: string; amount?: number; number: number }[]> = {};
      const streetActions = actionsFromDraft.filter(a=>a.street===street);
      streetActions.forEach((a, idx)=>{
        const seat = seatList?.find(p=>toLower(p.position)===toLower(a.playerName));
        if (!seat) return;
        map[seat.id] = [{ action: a.action, amount: a.amount, number: idx+1 }];
      });
      setLastActions(map);
      const highest = streetActions.reduce((m,a)=> (a.amount && a.amount>m? a.amount: m), 0);
      setCurrentBet(highest || 0);
    } else {
      // No actions, reset street UI state
      setLastActions({});
      setCurrentBet(0);
    }
    
    // Close the history dialog
    setShowHistory(false);
  };

  const resetForm = (nextPosition: string = '') => {
    setHandNote({
      sessionId: handNote.sessionId, // Keep session ID
      smallBlind: handNote.smallBlind, // Keep small blind
      bigBlind: handNote.bigBlind, // Keep big blind
      numberOfPlayers: handNote.numberOfPlayers, // Keep number of players
      position: nextPosition, // Set to next position
      heroCards: '', // Reset hero cards for the next hand
      heroPreflopNotes: '',
      heroFlopNotes: '',
      heroTurnNotes: '',
      heroRiverNotes: '',
      heroShowdownNotes: '',
      preflopNotes: '',
      flopCards: '',
      flopNotes: '',
      turnCard: '',
      turnNotes: '',
      riverCard: '',
      riverNotes: '',
      showdownNotes: '',
      handId: generateHandId(), // Generate a new handId for the new hand
      actions: []
    });
    
    // Reset editing state
    setIsEditing(false);
    setSelectedHandNote(null);
    setLastActions({}); // Explicitly clear last actions for the new hand

    // Fully reset betting/turn state
    setCurrentBet(0);
    setStraddleCount(0);
    setActivePickerPos(null);

    // Re-initialize players fresh for the new hand (base stacks, active seats)
    const availablePositions = getAvailablePositions();
    const bb = parseFloat(handNote.bigBlind) || 1;
    const sb = parseFloat(handNote.smallBlind) || (bb / 2);
    const newPlayersUnordered = availablePositions.map((pos, index) => ({
      id: index,
      position: pos.value,
      isActive: true,
      stackSize: (() => {
        let base = bb * 100;
        if (pos.value === 'sb') return Math.max(base - sb, 0);
        if (pos.value === 'bb') return Math.max(base - bb, 0);
        return base;
      })(),
    }));
    const orderedPlayers = sortPlayersPreflop(newPlayersUnordered);
    setPlayers(orderedPlayers);

    // Reset active player to first-to-act preflop
    const firstToActId = getFirstToActPreflop(orderedPlayers);
    if (firstToActId !== null) {
      setActivePlayerId(firstToActId);
    } else {
      setActivePlayerId(null);
    }
    
    setActiveTab('preflop');
    setOverrideSummaryJson(null);
  };

  const handleFoldClick = async () => {
    console.log('Hand folded');
    setIsSubmitting(true);
    
    try {
      // Update notes based on the current active tab
      let updatedHandNote = {...handNote};
      
      if (activeTab === 'preflop') {
        updatedHandNote.preflopNotes = 'Folded preflop';
        updatedHandNote.flopCards = '';
        updatedHandNote.flopNotes = '';
        updatedHandNote.turnCard = '';
        updatedHandNote.turnNotes = '';
        updatedHandNote.riverCard = '';
        updatedHandNote.riverNotes = '';
        updatedHandNote.showdownNotes = '';
      } else if (activeTab === 'flop') {
        updatedHandNote.flopNotes = updatedHandNote.flopNotes + ' (Folded on flop)';
        updatedHandNote.turnCard = '';
        updatedHandNote.turnNotes = '';
        updatedHandNote.riverCard = '';
        updatedHandNote.riverNotes = '';
        updatedHandNote.showdownNotes = '';
      } else if (activeTab === 'turn') {
        updatedHandNote.turnNotes = updatedHandNote.turnNotes + ' (Folded on turn)';
        updatedHandNote.riverCard = '';
        updatedHandNote.riverNotes = '';
        updatedHandNote.showdownNotes = '';
      } else if (activeTab === 'river') {
        updatedHandNote.riverNotes = updatedHandNote.riverNotes + ' (Folded on river)';
        updatedHandNote.showdownNotes = '';
      }
      
      // Also append a fold action to actions so it is persisted with any draft save
      const foldPlayer = handNote.position || players.find(p=>p.id===activePlayerId)?.position || 'HERO';
      const foldAction: Action = { playerName: foldPlayer, action: 'fold', amount: 0, street: activeTab };
      updatedHandNote.actions = [ ...(handNote.actions||[]), foldAction ];

      // Apply the updates
      setHandNote(updatedHandNote);

      // Build a summary and submit to analysis (quick fold)
      const newHandId = generateHandId();
      const enhancedNote: HandNote = {
        ...updatedHandNote,
        handId: newHandId,
        showdownCardsByPlayer,
        showdownMuckByPlayer,
        showdownWinners: Array.from(showdownWinners),
      };
      const summaryJsonObj = buildSummaryJson(enhancedNote, players, potSize);
      const summaryJsonStr = typeof summaryJsonObj === 'string' ? summaryJsonObj : JSON.stringify(summaryJsonObj);

      // Range context (optional)
      const rangeData = await fetchRangeForPosition(handNote.position);
      const normalizedItems = Array.isArray(rangeData?.range)
        ? rangeData!.range.map((it: any)=>({ hand: canonicalizeAnyPreflopHand(it.hand) || it.hand, call: it.call ?? 0, raise: it.raise ?? 0, fold: it.fold ?? 0 }))
        : null;
      const rangeContext = rangeData && normalizedItems ? { position: rangeData.position, pk: rangeData.pk, items: normalizedItems } : null;

      // Build hero action sequence and preflop fold flag
      const toLower = (s:string)=> (s||'').toLowerCase();
      const heroPosLower = toLower(handNote.position);
      const userActions = (updatedHandNote.actions || [])
        .filter(a => toLower(a.playerName) === heroPosLower)
        .map(a => ({ street: a.street, action: a.action }));
      const preflopFold = userActions.some(ua => ua.street === 'preflop' && toLower(ua.action) === 'fold');

      const functionParams:any = {
        handId: newHandId,
        summary: summaryJsonStr,
        handType: 'Live',
        sessionId: handNote.sessionId?.trim() || 'adhoc',
        shouldAwardXP: true,
        smallBlind: parseFloat(handNote.smallBlind)||0,
        bigBlind: parseFloat(handNote.bigBlind)||0,
        rangeContext,
        userActions,
        preflopFold
      };

      const result = await PlayFab.executeCloudScript('analyzePokerHand', functionParams);
      if (!result.success) {
        throw new Error('Failed to submit quick-fold hand for analysis');
      }

      // Get the next position in the sequence
      const availablePositions = getAvailablePositions();
      let nextPosition = '';
      
      if (handNote.position) {
        const currentIndex = availablePositions.findIndex(pos => pos.value === handNote.position);
        if (currentIndex !== -1 && currentIndex < availablePositions.length - 1) {
          // Move to next position
          nextPosition = availablePositions[currentIndex + 1].value;
        } else {
          // If at the last position, cycle back to the first position
          nextPosition = availablePositions[0].value;
        }
      }
      
      // Reset form with next position
      resetForm(nextPosition);
      
      alert('Hand folded and sent for analysis successfully!');
    } catch (error) {
      console.error('Error submitting folded hand:', error);
      alert('Failed to submit folded hand. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      console.log("Submitting hand note with isEditing:", isEditing);
      // Always generate a fresh handId on submit (avoid reusing IDs from templates/drafts)
      const newHandId = generateHandId();
      console.log("Generating new handId for submission:", newHandId);
      const currentHandId = newHandId;
      
      // Call PlayFab CloudScript to save hand note using the PlayFab service
      // Build enriched summary JSON before sending
      const enhancedNote: HandNote = {
        ...handNote,
        handId: newHandId,
        showdownCardsByPlayer,
        showdownMuckByPlayer,
        showdownWinners: Array.from(showdownWinners),
      };
      const summaryJsonObj = overrideSummaryJson ? overrideSummaryJson : buildSummaryJson(enhancedNote, players, potSize);

      // Build userActions (hero-only) and detect preflop quick fold
      const toLower = (s: string) => (s || '').toLowerCase();
      const heroPosLower = toLower(handNote.position);
      const userActions = (handNote.actions || [])
        .filter(a => toLower(a.playerName) === heroPosLower)
        .map(a => ({ street: a.street, action: a.action }));
      const preflopFold = userActions.some(ua => ua.street === 'preflop' && toLower(ua.action) === 'fold');

      // Fetch 200bb range for hero position and attach to request context (non-blocking if fails)
      const rangeData = await fetchRangeForPosition(handNote.position);
      const normalizedItems = Array.isArray(rangeData?.range)
        ? rangeData!.range.map((it: any)=>({
            hand: canonicalizeAnyPreflopHand(it.hand) || it.hand,
            call: it.call ?? 0, raise: it.raise ?? 0, fold: it.fold ?? 0
          }))
        : null;
      const rangeContext = rangeData && normalizedItems
        ? { position: rangeData.position, pk: rangeData.pk, items: normalizedItems }
        : null;

      const summaryJsonStr = typeof summaryJsonObj === 'string' ? summaryJsonObj : JSON.stringify(summaryJsonObj);

      const functionParams:any = {
        handId: newHandId,
        summary: summaryJsonStr,
        handType: 'Live',
        sessionId: handNote.sessionId?.trim() || 'adhoc',
        shouldAwardXP: true,
        smallBlind: parseFloat(handNote.smallBlind)||0,
        bigBlind: parseFloat(handNote.bigBlind)||0,
        rangeContext,
        // New: include hero action sequence and preflop quick fold flag
        userActions,
        preflopFold
      };

      const result = await PlayFab.executeCloudScript('analyzePokerHand', functionParams);
      
      // Log the result to see what's happening
      console.log('CloudScript result:', result);
      
      if (!result.success) {
        throw new Error("Failed to save hand note");
      }
      try {
        const cachePayload = {
          handNote: enhancedNote,
          players,
          potSize,
          summary: typeof summaryJsonObj === 'string' ? JSON.parse(summaryJsonObj) : summaryJsonObj,
          timestamp: new Date().toISOString(),
        };
        localStorage.setItem(LAST_HAND_KEY, JSON.stringify(cachePayload));
      } catch (cacheErr) {
        console.warn('Failed to cache last hand locally:', cacheErr);
      }
      
      console.log('Hand note saved via CloudScript:', result.data);

      // Navigate to the step-through hand review with the summary and handId
      try {
        // Intentionally stay on Hand Notes page after submission
      } catch (navErr) {
        console.warn('Navigation to replayer failed:', navErr);
      }
      
      // Check if the returned handId matches what we sent
      const returnedHandId = result.data?.FunctionResult?.handId;
      if (returnedHandId && returnedHandId !== currentHandId) {
        console.warn('WARNING: Server returned a different handId than we sent!', {
          sent: currentHandId,
          received: returnedHandId
        });
      } else {
        console.log('Server preserved our handId correctly:', currentHandId);
      }
      
      // Get the next position in the sequence
      const availablePositions = getAvailablePositions();
      let nextPosition = '';
      
      if (handNote.position) {
        const currentIndex = availablePositions.findIndex(pos => pos.value === handNote.position);
        if (currentIndex !== -1 && currentIndex < availablePositions.length - 1) {
          // Move to next position
          nextPosition = availablePositions[currentIndex + 1].value;
        } else {
          // If at the last position, cycle back to the first position
          nextPosition = availablePositions[0].value;
        }
      }
      
      // Reset form with next position
      resetForm(nextPosition);
      
      alert('Hand note submitted successfully!');
    } catch (error) {
      console.error('Error submitting hand note:', error);
      alert('Failed to submit hand note. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Apply a pasted/loaded template object to UI state and optionally override summary submit
  const applyTemplateObject = (obj: any) => {
    try {
      if (!obj || typeof obj !== 'object') return;
      const hasSummary = !!(obj.summary || obj.summaryJson || (obj.metadata && (obj.preflop || obj.flop)));
      const summaryObj = obj.summary || obj.summaryJson || (hasSummary ? obj : null);
      if (summaryObj) {
        setOverrideSummaryJson(summaryObj);
      }

      // Try to extract a note-like object
      const noteLike = obj.handNote || obj.note || (
        obj.sessionId ||
        obj.actions ||
        (typeof obj.heroCards === 'string') ||
        obj.flopCards || obj.turnCard || obj.riverCard
          ? obj
          : null
      );
      const playersLike: any[] | null = Array.isArray(obj.players) ? obj.players : null;
      const potLike: number | undefined = typeof obj.potSize === 'number' ? obj.potSize : undefined;

      if (noteLike) {
        setHandNote(prev => ({
          ...prev,
          sessionId: noteLike.sessionId || prev.sessionId,
          smallBlind: noteLike.smallBlind || prev.smallBlind,
          bigBlind: noteLike.bigBlind || prev.bigBlind,
          numberOfPlayers: noteLike.numberOfPlayers || prev.numberOfPlayers,
          position: noteLike.position || prev.position,
          heroCards: normalizeCardsString(noteLike.heroCards) || prev.heroCards,
          heroPreflopNotes: noteLike.heroPreflopNotes || prev.heroPreflopNotes,
          heroFlopNotes: noteLike.heroFlopNotes || prev.heroFlopNotes,
          heroTurnNotes: noteLike.heroTurnNotes || prev.heroTurnNotes,
          heroRiverNotes: noteLike.heroRiverNotes || prev.heroRiverNotes,
          heroShowdownNotes: noteLike.heroShowdownNotes || prev.heroShowdownNotes,
          preflopNotes: noteLike.preflopNotes || prev.preflopNotes,
          flopCards: noteLike.flopCards || prev.flopCards,
          flopNotes: noteLike.flopNotes || prev.flopNotes,
          turnCard: noteLike.turnCard || prev.turnCard,
          turnNotes: noteLike.turnNotes || prev.turnNotes,
          riverCard: noteLike.riverCard || prev.riverCard,
          riverNotes: noteLike.riverNotes || prev.riverNotes,
          showdownNotes: noteLike.showdownNotes || prev.showdownNotes,
          handId: noteLike.handId || prev.handId,
          actions: Array.isArray(noteLike.actions) ? noteLike.actions : prev.actions,
        }));

        const noteActions: Action[] = Array.isArray(noteLike.actions) ? noteLike.actions : [];
        if (playersLike) {
          skipAutoInitPlayersRef.current = true;
          const toLower = (s:string)=> (s||'').toLowerCase();
          const foldedPositions = new Set(
            noteActions.filter(a=>toLower(a.action)==='fold').map(a=>toLower(a.playerName))
          );
          const mergedPlayers = playersLike.map((p:any, index:number)=>({
            id: typeof p.id === 'number' ? p.id : index,
            position: (p.position || p.name || '').toLowerCase(),
            isActive: p.isActive !== false && !foldedPositions.has(toLower(p.position||p.name||'')),
            stackSize: typeof p.stackSize === 'number' ? p.stackSize : (typeof p.initialStack === 'number' ? p.initialStack : 0),
          }));
          setPlayers(sortPlayersPreflop(mergedPlayers));
        }

        if (typeof potLike === 'number') setPotSize(potLike);

        // Winners from note or summary
        const winnersFromNote: any[] | null = Array.isArray((noteLike as any)?.showdownWinners) ? (noteLike as any).showdownWinners : null;
        const winnersFromSummary: any[] | null = Array.isArray((summaryObj as any)?.results?.winners) ? (summaryObj as any).results.winners : null;
        const winnersSource: any[] | null = winnersFromNote || winnersFromSummary || null;
        if (winnersSource) {
          const toLower = (s:any)=> String(s||'').toLowerCase();
          const candidatePlayers: any[] = playersLike || players;
          const winnersSet = new Set<string>();
          winnersSource.forEach((w:any)=>{
            if (typeof w === 'string') {
              winnersSet.add(toLower(w));
            } else if (typeof w === 'number') {
              const pl = candidatePlayers?.find((p:any)=> p && typeof p.id==='number' && p.id===w);
              if (pl?.position) winnersSet.add(toLower(pl.position));
            }
          });
          if (winnersSet.size>0) setShowdownWinners(winnersSet);
        }

        const STREET_ORDER: StreetTab[] = ['preflop','flop','turn','river','showdown'];
        const latestStreetIdx = Math.max(-1, ...noteActions.map(a=>STREET_ORDER.indexOf(a.street as StreetTab)).filter(i=>i>=0));
        if (latestStreetIdx >= 0) {
          const street = STREET_ORDER[latestStreetIdx];
          setActiveTab(street);
          const seatList: any[] = playersLike || players;
          const map: Record<number, { action: string; amount?: number; number: number }[]> = {};
          const streetActions = noteActions.filter(a=>a.street===street);
          streetActions.forEach((a, idx)=>{
            const seat = seatList?.find((p:any)=> (p.position||'').toLowerCase() === (a.playerName||'').toLowerCase());
            if (!seat) return;
            const seatId = typeof seat.id === 'number' ? seat.id : idx;
            map[seatId] = [{ action: a.action, amount: a.amount, number: idx+1 }];
          });
          setLastActions(map);
          const highest = streetActions.reduce((m,a)=> (a.amount && a.amount>m? a.amount: m), 0);
          setCurrentBet(highest || 0);
        } else {
          setLastActions({});
          setCurrentBet(0);
        }
      }
      if (summaryObj) {
        // reflect cards from summary onto UI
        const hc = summaryObj?.heroCards;
        const board: string[] = Array.isArray(summaryObj?.board) ? summaryObj.board : [];
        if (hc && hc.holeCard1 && hc.holeCard2) {
          const heroCards = String(hc.holeCard1) + String(hc.holeCard2);
          setHandNote(prev=>({...prev, heroCards}));
        }
        if (board.length>=3) setHandNote(prev=>({...prev, flopCards: (board[0]||'')+(board[1]||'')+(board[2]||'') }));
        if (board.length>=4) setHandNote(prev=>({...prev, turnCard: board[3]||'' }));
        if (board.length>=5) setHandNote(prev=>({...prev, riverCard: board[4]||'' }));

        // Winners from summary-only template
        const winnersFromSummaryOnly: any[] | null = Array.isArray((summaryObj as any)?.results?.winners) ? (summaryObj as any).results.winners : null;
        const winnersFromRoot: any[] | null = Array.isArray((summaryObj as any)?.showdownWinners) ? (summaryObj as any).showdownWinners : null;
        if (winnersFromSummaryOnly || winnersFromRoot) {
          const toLower = (s:any)=> String(s||'').toLowerCase();
          const winnersSet = new Set<string>();
          (winnersFromSummaryOnly||[]).forEach((w:any)=>{ if (typeof w==='string') winnersSet.add(toLower(w)); });
          (winnersFromRoot||[]).forEach((w:any)=>{ if (typeof w==='string') winnersSet.add(toLower(w)); });
          if (winnersSet.size>0) setShowdownWinners(winnersSet);
        }

        // If summary contains players, hydrate players and hero position
        try {
          const sumPlayers: any[] | null = Array.isArray((summaryObj as any)?.players) ? (summaryObj as any).players : null;
          if (sumPlayers) {
            skipAutoInitPlayersRef.current = true;
            const mappedPlayers = sumPlayers.map((p:any, index:number)=>({
              id: typeof p.id === 'number' ? p.id : index,
              position: String(p.position || p.name || '').toLowerCase(),
              isActive: true,
              stackSize: typeof p.initialStack === 'number' ? p.initialStack : (typeof p.stackSize==='number'?p.stackSize:0),
            }));
            setPlayers(sortPlayersPreflop(mappedPlayers));

            const heroEntry = sumPlayers.find((p:any)=> p?.isHero);
            if (heroEntry && (heroEntry.position || heroEntry.name)) {
              const heroPos = String(heroEntry.position || heroEntry.name).toLowerCase();
              setHandNote(prev=>({ ...prev, position: heroPos }));
            }
          }
        } catch {}

        // Hydrate blinds and pot from summary metadata if available
        try {
          const sb = (summaryObj?.metadata?.stakes?.smallBlind);
          const bb = (summaryObj?.metadata?.stakes?.bigBlind);
          if (typeof sb === 'number' || typeof sb === 'string' || typeof bb === 'number' || typeof bb === 'string') {
            setHandNote(prev=>({
              ...prev,
              smallBlind: sb !== undefined ? String(sb) : prev.smallBlind,
              bigBlind: bb !== undefined ? String(bb) : prev.bigBlind,
            }));
          }
          if (summaryObj?.metadata?.handId) {
            setHandNote(prev=>({ ...prev, handId: String(summaryObj.metadata.handId) }));
          }
        } catch {}
        try {
          if (typeof (summaryObj as any)?.finalPot === 'number') setPotSize((summaryObj as any).finalPot);
        } catch {}
        // Showdown mappings from summary
        try {
          if (summaryObj?.showdownCardsByPlayer && typeof summaryObj.showdownCardsByPlayer === 'object') {
            const obj = summaryObj.showdownCardsByPlayer as Record<string,string>;
            const normalized: Record<string,string> = {};
            Object.keys(obj).forEach(k=>{ normalized[String(k).toLowerCase()] = String(obj[k]); });
            setShowdownCardsByPlayer(normalized);
          }
          if (summaryObj?.showdownMuckByPlayer && typeof summaryObj.showdownMuckByPlayer === 'object') {
            const obj = summaryObj.showdownMuckByPlayer as Record<string,boolean>;
            const normalized: Record<string,boolean> = {};
            Object.keys(obj).forEach(k=>{ normalized[String(k).toLowerCase()] = !!obj[k]; });
            setShowdownMuckByPlayer(normalized);
          }
        } catch {}

        // Derive UI actions from summary per-street actions if present
        try {
          type StreetKey = 'preflop'|'flop'|'turn'|'river';
          const STREET_KEYS: StreetKey[] = ['preflop','flop','turn','river'];
          const playersSource: any[] | null = Array.isArray((summaryObj as any)?.players) ? (summaryObj as any).players : null;
          const mapPlayerIdToPos = (id:number): string => {
            const f = playersSource?.find(p=>p.id===id);
            return f ? String(f.position || f.name || '').toLowerCase() : '';
          };
          const nextActions: Action[] = [];
          STREET_KEYS.forEach((street)=>{
            const acts: any[] = Array.isArray((summaryObj as any)?.[street]?.actions) ? (summaryObj as any)[street].actions : [];
            acts.forEach((a:any)=>{
              const playerId = typeof a.playerId === 'number' ? a.playerId : (typeof a.playerID==='number'?a.playerID:undefined);
              const playerName = a.playerName ? String(a.playerName).toLowerCase() : (playerId!==undefined ? mapPlayerIdToPos(playerId) : '');
              const type = a.type || a.action || '';
              const amount = typeof a.amount === 'number' ? a.amount : (typeof a.amount === 'string' ? parseFloat(a.amount) : undefined);
              if (playerName && type) {
                nextActions.push({ playerName, action: String(type).toLowerCase(), amount, street });
              }
            });
          });
          if (nextActions.length > 0) {
            setHandNote(prev=>({ ...prev, actions: nextActions }));

            // Mark folded seats as inactive globally (for showdown filtering)
            const foldedPositions = new Set<string>(
              nextActions
                .filter(a => String(a.action).toLowerCase() === 'fold')
                .map(a => String(a.playerName || '').toLowerCase())
            );
            if (playersSource) {
              const updatedPlayers = playersSource.map((p:any, index:number)=>({
                id: typeof p.id === 'number' ? p.id : index,
                position: String(p.position || p.name || '').toLowerCase(),
                isActive: !foldedPositions.has(String(p.position || p.name || '').toLowerCase()),
                stackSize: typeof p.initialStack === 'number' ? p.initialStack : (typeof p.stackSize==='number'?p.stackSize:0),
              }));
              setPlayers(sortPlayersPreflop(updatedPlayers));
            } else {
              setPlayers(prev => prev.map(p => ({
                ...p,
                isActive: !foldedPositions.has(String(p.position||'').toLowerCase())
              })));
            }

            // Always switch to latest street present to make loaded board/actions visible
            const order: StreetTab[] = ['preflop','flop','turn','river','showdown'];
            const latestIdx = Math.max(-1, ...nextActions.map(a=> order.indexOf(a.street as StreetTab)).filter(i=>i>=0));
            const toLower = (s:string)=> (s||'').toLowerCase();
            const seatList: any[] = playersSource || players;

            const buildForStreet = (street: StreetTab) => {
              const map: Record<number, { action: string; amount?: number; number: number }[]> = {};
              const streetActions = nextActions.filter(a=>a.street===street);
              streetActions.forEach((a, idx)=>{
                const seat = seatList?.find((p:any)=> toLower(p.position||p.name||'') === toLower(a.playerName));
                if (!seat) return;
                const seatId = typeof seat.id === 'number' ? seat.id : idx;
                map[seatId] = [{ action: a.action, amount: a.amount, number: idx+1 }];
              });
              setLastActions(map);
              const highest = streetActions.reduce((m,a)=> (a.amount && a.amount>m? a.amount: m), 0);
              setCurrentBet(highest || 0);
            };

            if (latestIdx >= 0) {
              const latestStreet = order[latestIdx] as StreetTab;
              setActiveTab(latestStreet);
              buildForStreet(latestStreet);
            }
          }
        } catch {}
      }
    } catch (err) {
      console.error('Failed to apply template object:', err);
      alert('Failed to apply template. Please check JSON structure.');
    }
  };

  const loadLastHandFromLocal = () => {
    try {
      const raw = localStorage.getItem(LAST_HAND_KEY);
      if (!raw) { alert('No last hand found.'); return; }
      const obj = JSON.parse(raw);
      applyTemplateObject(obj);
    } catch (err) {
      console.error('Failed to load last hand from local storage:', err);
      alert('Could not load last hand.');
    }
  };

  const openViewTemplate = () => {
    try {
      const enhancedNote: HandNote = {
        ...handNote,
        showdownCardsByPlayer,
        showdownMuckByPlayer,
        showdownWinners: Array.from(showdownWinners),
      };
      const summaryObj = overrideSummaryJson ? overrideSummaryJson : buildSummaryJson(enhancedNote, players, potSize);
      const jsonStr = typeof summaryObj === 'string' ? summaryObj : JSON.stringify(summaryObj, null, 2);
      setViewTemplateText(jsonStr);
      setIsViewTemplateOpen(true);
    } catch (err) {
      console.error('Failed generating hand template JSON:', err);
      alert('Could not generate template JSON.');
    }
  };

  const sessionInfoText = () => {
    console.log("Current handNote values:", handNote);
    
    const sessionPart = handNote.sessionId 
      ? `Session: ${handNote.sessionId}` 
      : "Session: Not set";
    
    const blindsPart = handNote.smallBlind || handNote.bigBlind
      ? `Blinds: ${handNote.smallBlind || '?'}/${handNote.bigBlind || '?'}`
      : "";
    
    const playersPart = handNote.numberOfPlayers
      ? `${handNote.numberOfPlayers}-MAX`
      : "";
    
    // Combine parts, only adding separators when needed
    let result = sessionPart;
    if (blindsPart) result += ` - ${blindsPart}`;
    if (playersPart) result += ` - ${playersPart}`;
    
    return result;
  };

  // Get positions based on player count
  const getAvailablePositions = () => {
      const playerCount = parseInt(handNote.numberOfPlayers, 10);
  const POSITIONS_BY_COUNT: Record<number, { value: string; label: string }[]> = {
    4: ALL_POSITIONS.filter(p=>['bb','sb','btn','utg'].includes(p.value)),
    5: ALL_POSITIONS.filter(p=>['bb','sb','btn','co','utg'].includes(p.value)),
    6: ALL_POSITIONS.filter(p=>['bb','sb','btn','co','mp','utg'].includes(p.value)),
    7: ALL_POSITIONS.filter(p=>['bb','sb','btn','co','hj','mp','utg'].includes(p.value)),
    8: ALL_POSITIONS.filter(p=>['bb','sb','btn','co','hj','mp1','mp','utg'].includes(p.value)),
    9: ALL_POSITIONS.filter(p=>['bb','sb','btn','co','hj','mp1','mp','utg1','utg'].includes(p.value)),
    10: ALL_POSITIONS.filter(p=>['bb','sb','btn','co','hj','mp2','mp1','mp','utg1','utg'].includes(p.value)),
  };
  return POSITIONS_BY_COUNT[playerCount] || ALL_POSITIONS.filter(pos => pos.minPlayers <= playerCount).slice(0,playerCount);
};

  const getActiveTabAbbreviation = () => {
    switch(activeTab) {
      case 'preflop': return 'PF';
      case 'flop': return 'F';
      case 'turn': return 'T';
      case 'river': return 'R';
      case 'showdown': return 'S';
      default: return 'PF';
    }
  };

  const getHeroCardDisplay = () => {
    const firstCard = handNote.heroCards.substring(0, 2);
    const secondCard = handNote.heroCards.substring(2, 4);
    
    return (
      <div className="card-display" style={{position:'relative'}}>
        {/* Quick Fold button removed from hero cards area; available in top menu */}
        <CardPickerDialog 
          cardPosition="card1"
          currentCard={firstCard}
          activePickerPos={activePickerPos}
          setActivePickerPos={setActivePickerPos}
          onCardSelect={(card, position) => {
            // Update first card
            const secondCardPart = handNote.heroCards.substring(2, 4);
            setHandNote(prev => ({
              ...prev,
              heroCards: card + secondCardPart
            }));
            // auto-open second card if empty
            if(position==='card1' && !secondCard){
              setActivePickerPos('card2');
            }
          }}
        />
        
        <CardPickerDialog 
          cardPosition="card2"
          currentCard={secondCard}
          activePickerPos={activePickerPos}
          setActivePickerPos={setActivePickerPos}
          onCardSelect={(card, position) => {
            // Update second card
            const firstCardPart = handNote.heroCards.substring(0, 2);
            setHandNote(prev => ({
              ...prev,
              heroCards: firstCardPart + card
            }));
            setActivePickerPos(null);
          }}
        />
        {/* Hero notes button removed from hero cards area; modal can be opened from top menu */}
        {/* Hero Notes Dialog */}
        <Dialog.Root open={notesModal==='hero'} onOpenChange={open=>setNotesModal(open?'hero':null)}>
          <Dialog.Portal>
            <Dialog.Overlay className="card-dialog-overlay" />
            <Dialog.Content className="card-dialog-content" style={{maxWidth:300}}>
              <Dialog.Title className="card-dialog-title">Hand Notes</Dialog.Title>
              <Tabs.Root defaultValue="preflop" className="tabs-root" orientation="horizontal">
                <Tabs.List className="tabs-list">
                  <Tabs.Trigger value="preflop" className="tabs-trigger">Preflop</Tabs.Trigger>
                  <Tabs.Trigger value="flop" className="tabs-trigger">Flop</Tabs.Trigger>
                  <Tabs.Trigger value="turn" className="tabs-trigger">Turn</Tabs.Trigger>
                  <Tabs.Trigger value="river" className="tabs-trigger">River</Tabs.Trigger>
                  <Tabs.Trigger value="showdown" className="tabs-trigger">Showdown</Tabs.Trigger>
                </Tabs.List>
                <Tabs.Content value="preflop" className="tabs-content">
                  <textarea
                    value={handNote.heroPreflopNotes}
                    onChange={e=>setHandNote(prev=>({...prev, heroPreflopNotes:e.target.value}))}
                    style={{width:'100%',height:120}}
                  />
                </Tabs.Content>
                <Tabs.Content value="flop" className="tabs-content">
                  <textarea
                    value={handNote.heroFlopNotes}
                    onChange={e=>setHandNote(prev=>({...prev, heroFlopNotes:e.target.value}))}
                    style={{width:'100%',height:120}}
                  />
                </Tabs.Content>
                <Tabs.Content value="turn" className="tabs-content">
                  <textarea
                    value={handNote.heroTurnNotes}
                    onChange={e=>setHandNote(prev=>({...prev, heroTurnNotes:e.target.value}))}
                    style={{width:'100%',height:120}}
                  />
                </Tabs.Content>
                <Tabs.Content value="river" className="tabs-content">
                  <textarea
                    value={handNote.heroRiverNotes}
                    onChange={e=>setHandNote(prev=>({...prev, heroRiverNotes:e.target.value}))}
                    style={{width:'100%',height:120}}
                  />
                </Tabs.Content>
                <Tabs.Content value="showdown" className="tabs-content">
                  <textarea
                    value={handNote.heroShowdownNotes}
                    onChange={e=>setHandNote(prev=>({...prev, heroShowdownNotes:e.target.value}))}
                    style={{width:'100%',height:120}}
                  />
                </Tabs.Content>
              </Tabs.Root>
              <Dialog.Close asChild>
                <button className="card-dialog-close" aria-label="Close"><BsX/></button>
              </Dialog.Close>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </div>
    );
  };
  
  const getBoardCardDisplay = () => {
    const flopCard1 = handNote.flopCards.substring(0, 2);
    const flopCard2 = handNote.flopCards.substring(2, 4);
    const flopCard3 = handNote.flopCards.substring(4, 6);
    const turnCard = handNote.turnCard;
    const riverCard = handNote.riverCard;
    
    // Display different cards based on active tab/street
    if (activeTab === 'flop') {
      return (
        <div className="board-display">
          <CardPickerDialog 
            cardPosition="flop1"
            currentCard={flopCard1}
            activePickerPos={activePickerPos}
            setActivePickerPos={setActivePickerPos}
            onCardSelect={(card) => {
              const card2 = handNote.flopCards.substring(2, 4);
              const card3 = handNote.flopCards.substring(4, 6);
              setHandNote(prev => ({
                ...prev,
                flopCards: card + card2 + card3
              }));
              if(!flopCard2){setActivePickerPos('flop2');}
            }}
          />
          <CardPickerDialog 
            cardPosition="flop2"
            currentCard={flopCard2}
            activePickerPos={activePickerPos}
            setActivePickerPos={setActivePickerPos}
            onCardSelect={(card) => {
              const card1 = handNote.flopCards.substring(0, 2);
              const card3 = handNote.flopCards.substring(4, 6);
              setHandNote(prev => ({
                ...prev,
                flopCards: card1 + card + card3
              }));
              if(!flopCard3){setActivePickerPos('flop3');}
            }}
          />
          <CardPickerDialog 
            cardPosition="flop3"
            currentCard={flopCard3}
            activePickerPos={activePickerPos}
            setActivePickerPos={setActivePickerPos}
            onCardSelect={(card) => {
              const card1 = handNote.flopCards.substring(0, 2);
              const card2 = handNote.flopCards.substring(2, 4);
              setHandNote(prev => ({
                ...prev,
                flopCards: card1 + card2 + card
              }));
              if(!flopCard1){setActivePickerPos('flop1');}
            }}
          />
        </div>
      );
    } 
    else if (activeTab === 'turn') {
      return (
        <div className="board-display">
          {/* Show flop cards (still editable) */}
          <CardPickerDialog 
            cardPosition="flop1"
            currentCard={flopCard1}
            activePickerPos={activePickerPos}
            setActivePickerPos={setActivePickerPos}
            onCardSelect={(card) => {
              const card2 = handNote.flopCards.substring(2, 4);
              const card3 = handNote.flopCards.substring(4, 6);
              setHandNote(prev => ({
                ...prev,
                flopCards: card + card2 + card3
              }));
              if(!flopCard2){setActivePickerPos('flop2');}
            }}
          />
          <CardPickerDialog 
            cardPosition="flop2"
            currentCard={flopCard2}
            activePickerPos={activePickerPos}
            setActivePickerPos={setActivePickerPos}
            onCardSelect={(card) => {
              const card1 = handNote.flopCards.substring(0, 2);
              const card3 = handNote.flopCards.substring(4, 6);
              setHandNote(prev => ({
                ...prev,
                flopCards: card1 + card + card3
              }));
              if(!flopCard3){setActivePickerPos('flop3');}
            }}
          />
          <CardPickerDialog 
            cardPosition="flop3"
            currentCard={flopCard3}
            activePickerPos={activePickerPos}
            setActivePickerPos={setActivePickerPos}
            onCardSelect={(card) => {
              const card1 = handNote.flopCards.substring(0, 2);
              const card2 = handNote.flopCards.substring(2, 4);
              setHandNote(prev => ({
                ...prev,
                flopCards: card1 + card2 + card
              }));
              if(!flopCard1){setActivePickerPos('flop1');}
            }}
          />
          
          {/* Turn card - editable */}
          <CardPickerDialog 
            cardPosition="turn"
            currentCard={turnCard}
            activePickerPos={activePickerPos}
            setActivePickerPos={setActivePickerPos}
            onCardSelect={(card) => {
              setHandNote(prev => ({
                ...prev,
                turnCard: card
              }));
            }}
          />
        </div>
      );
    }
    else if (activeTab === 'river' || activeTab === 'showdown') {
      return (
        <div className="board-display">
          {/* Show flop cards (still editable) */}
          <CardPickerDialog 
            cardPosition="flop1"
            currentCard={flopCard1}
            activePickerPos={activePickerPos}
            setActivePickerPos={setActivePickerPos}
            onCardSelect={(card) => {
              const card2 = handNote.flopCards.substring(2, 4);
              const card3 = handNote.flopCards.substring(4, 6);
              setHandNote(prev => ({
                ...prev,
                flopCards: card + card2 + card3
              }));
              if(!flopCard2){setActivePickerPos('flop2');}
            }}
          />
          <CardPickerDialog 
            cardPosition="flop2"
            currentCard={flopCard2}
            activePickerPos={activePickerPos}
            setActivePickerPos={setActivePickerPos}
            onCardSelect={(card) => {
              const card1 = handNote.flopCards.substring(0, 2);
              const card3 = handNote.flopCards.substring(4, 6);
              setHandNote(prev => ({
                ...prev,
                flopCards: card1 + card + card3
              }));
              if(!flopCard3){setActivePickerPos('flop3');}
            }}
          />
          <CardPickerDialog 
            cardPosition="flop3"
            currentCard={flopCard3}
            activePickerPos={activePickerPos}
            setActivePickerPos={setActivePickerPos}
            onCardSelect={(card) => {
              const card1 = handNote.flopCards.substring(0, 2);
              const card2 = handNote.flopCards.substring(2, 4);
              setHandNote(prev => ({
                ...prev,
                flopCards: card1 + card2 + card
              }));
              if(!flopCard1){setActivePickerPos('flop1');}
            }}
          />
          
          {/* Turn card (still editable) */}
          <CardPickerDialog 
            cardPosition="turn"
            currentCard={turnCard}
            activePickerPos={activePickerPos}
            setActivePickerPos={setActivePickerPos}
            onCardSelect={(card) => {
              setHandNote(prev => ({
                ...prev,
                turnCard: card
              }));
            }}
          />
          
          {/* River card - editable */}
          <CardPickerDialog 
            cardPosition="river"
            currentCard={riverCard}
            activePickerPos={activePickerPos}
            setActivePickerPos={setActivePickerPos}
            onCardSelect={(card) => {
              setHandNote(prev => ({
                ...prev,
                riverCard: card
              }));
            }}
          />
        </div>
      );
    }
    
    // Preflop: no board cards yet, render nothing for a cleaner table
    return null;
  };

  // Small context line under the board
  const renderBoardContextLine = () => {
    const streetLabel = activeTab.charAt(0).toUpperCase() + activeTab.slice(1);
    const text = getEnhancedHandContext();
    return (
      <div className="board-context-line">{`Street: ${streetLabel} — ${text}`}</div>
    );
  };

  // Enhance handEval with context like "top two pair" when applicable
  const getEnhancedHandContext = () => {
    const base = (handEval || '').trim();
    if (!base) return '-';

    try {
      const hero = handNote.heroCards || '';
      if (hero.length < 4) return base;
      const h1 = hero.substring(0, 2);
      const h2 = hero.substring(2, 4);

      // Build current board array by activeTab
      const boardArr: string[] = [];
      if (['flop', 'turn', 'river', 'showdown'].includes(activeTab)) {
        if (handNote.flopCards.length >= 6) {
          boardArr.push(
            handNote.flopCards.substring(0, 2),
            handNote.flopCards.substring(2, 4),
            handNote.flopCards.substring(4, 6)
          );
        }
      }
      if (['turn', 'river', 'showdown'].includes(activeTab)) {
        if (handNote.turnCard.length === 2) boardArr.push(handNote.turnCard);
      }
      if (['river', 'showdown'].includes(activeTab)) {
        if (handNote.riverCard.length === 2) boardArr.push(handNote.riverCard);
      }
      if (boardArr.length === 0) return base;

      const rankOrder = ['2','3','4','5','6','7','8','9','t','j','q','k','a'];
      const ord = (r: string) => rankOrder.indexOf(r.toLowerCase());
      const rankOf = (card: string) => (card && card.length === 2 ? card[0] : '');

      // Quick enrich for two pair: detect "top two pair" when both hole ranks match the top two distinct board ranks
      const isTwoPair = /two\s*pair/i.test(base);
      if (isTwoPair) {
        const boardRanks = Array.from(new Set(boardArr.map(rankOf)));
        if (boardRanks.length >= 2) {
          // sort by strength descending
          boardRanks.sort((a, b) => ord(b) - ord(a));
          const top2 = boardRanks.slice(0, 2);
          const holeRanks = [rankOf(h1), rankOf(h2)];
          const bothPairWithBoard = holeRanks.every(hr => boardRanks.includes(hr)) && holeRanks[0] !== holeRanks[1];
          const matchesTopTwo = new Set(holeRanks).size === 2 && top2.every(r => holeRanks.includes(r));
          if (bothPairWithBoard && matchesTopTwo) {
            // Replace leading "two pair" with "top two pair" if present; otherwise prefix
            const replaced = base.replace(/two\s*pair/i, 'Top two pair');
            return replaced === base ? `Top two pair — ${base}` : replaced;
          }
        }
      }

      // Quick enrich for single pair: label top pair when hole rank equals highest board rank and not a board-paired case
      const isPair = /\bpair\b/i.test(base) && !/two\s*pair|three|full|four/i.test(base);
      if (isPair) {
        const boardRanks = Array.from(new Set(boardArr.map(rankOf)));
        if (boardRanks.length >= 1) {
          boardRanks.sort((a, b) => ord(b) - ord(a));
          const top = boardRanks[0];
          const holeRanks = [rankOf(h1), rankOf(h2)];
          if (holeRanks.includes(top)) {
            if (!/^top\s*pair/i.test(base)) {
              return `Top pair — ${base}`;
            }
          }
        }
      }

      return base;
    } catch {
      return base;
    }
  };

  // Card Picker Dialog Component
  interface CardPickerDialogProps {
    cardPosition: CardPosition;
    currentCard: string;
    activePickerPos: CardPosition | null;
    setActivePickerPos: (pos: CardPosition | null) => void;
    onCardSelect: (card: string, position: CardPosition) => void;
  }
  
  const CARD_CYCLE_ORDER: CardPosition[] = ['card1','card2','flop1','flop2','flop3','turn','river'];

  const CardPickerDialog: React.FC<CardPickerDialogProps> = ({ 
    cardPosition, 
    currentCard, 
    activePickerPos,
    setActivePickerPos,
    onCardSelect 
  }) => {
    const open = activePickerPos === cardPosition;
    const [selectedRank, setSelectedRank] = useState<string | null>(null);
    
    // Get all cards currently in use (except the current one being edited)
    const getUsedCards = (): string[] => {
      const usedCards: string[] = [];
      
      // Add hero cards
      const firstHeroCard = handNote.heroCards.substring(0, 2);
      const secondHeroCard = handNote.heroCards.substring(2, 4);
      if (firstHeroCard && cardPosition !== 'card1') usedCards.push(firstHeroCard);
      if (secondHeroCard && cardPosition !== 'card2') usedCards.push(secondHeroCard);
      
      // Add flop cards
      const flop1 = handNote.flopCards.substring(0, 2);
      const flop2 = handNote.flopCards.substring(2, 4);
      const flop3 = handNote.flopCards.substring(4, 6);
      if (flop1 && cardPosition !== 'flop1') usedCards.push(flop1);
      if (flop2 && cardPosition !== 'flop2') usedCards.push(flop2);
      if (flop3 && cardPosition !== 'flop3') usedCards.push(flop3);
      
      // Add turn card
      if (handNote.turnCard && cardPosition !== 'turn') usedCards.push(handNote.turnCard);
      
      // Add river card
      if (handNote.riverCard && cardPosition !== 'river') usedCards.push(handNote.riverCard);
      
      return usedCards;
    };
    
    // Check if a specific card (rank+suit) is already used
    const isCardUsed = (rank: string, suit: string): boolean => {
      const card = rank + suit;
      return getUsedCards().includes(card);
    };
    
    const handleRankSelection = (rank: string) => {
      setSelectedRank(rank);
    };
    
    const handleSuitSelection = (suit: string) => {
      if (!selectedRank) return;
      
      const card = selectedRank + suit;
      onCardSelect(card, cardPosition);
      setSelectedRank(null);
      
      // Determine next card position in cycle but stay within same logical street group
      const groupOf = (pos: CardPosition): string => {
        if(pos === 'card1' || pos === 'card2') return 'hero';
        if(pos.startsWith('flop')) return 'flop';
        return pos; // 'turn' or 'river'
      };
      const currentIndex = CARD_CYCLE_ORDER.indexOf(cardPosition);
      const currentGroup = groupOf(cardPosition);
      for(let i = currentIndex + 1; i < CARD_CYCLE_ORDER.length; i++) {
        const nextPos = CARD_CYCLE_ORDER[i];
        if(groupOf(nextPos) !== currentGroup) break; // stop cycling when group ends
        const val = (() => {
          switch(nextPos) {
            case 'card1': return handNote.heroCards.substring(0,2);
            case 'card2': return handNote.heroCards.substring(2,4);
            case 'flop1': return handNote.flopCards.substring(0,2);
            case 'flop2': return handNote.flopCards.substring(2,4);
            case 'flop3': return handNote.flopCards.substring(4,6);
            case 'turn': return handNote.turnCard;
            case 'river': return handNote.riverCard;
            default: return '';
          }
        })();
        if(val.length !== 2) {
          setActivePickerPos(nextPos);
          return;
        }
      }
      // no further cards or all filled
      setActivePickerPos(null);
    };
    
    return (
      <Dialog.Root open={open} onOpenChange={(v) => setActivePickerPos(v ? cardPosition : null)}>
        <Dialog.Trigger asChild>
          <div 
            className={`card ${currentCard ? 'filled' : 'empty'}`} 
            onClick={() => {
              setActivePickerPos(cardPosition);
              
            }}
          >
            {formatCard(currentCard) || '??'}
          </div>
        </Dialog.Trigger>
        
        <Dialog.Portal>
          <Dialog.Overlay className="card-dialog-overlay" />
          <Dialog.Content className="card-dialog-content">
            <Dialog.Title className="card-dialog-title">
              Select {cardPosition === 'card1' ? 'First' : cardPosition === 'card2' ? 'Second' : cardPosition === 'flop1' ? 'Flop 1' : cardPosition === 'flop2' ? 'Flop 2' : cardPosition === 'flop3' ? 'Flop 3' : cardPosition === 'turn' ? 'Turn' : 'River'} Card
            </Dialog.Title>
            
            <div className="card-dialog-body">
              {!selectedRank ? (
                <>
                  <div className="card-dialog-label">Select Rank</div>
                  <div className="rank-picker-horizontal">
                    {DISPLAY_RANKS.map(rank => (
                      <button 
                        key={rank} 
                        className="rank-button" 
                        onClick={() => handleRankSelection(rank)}
                      >
                        {rank}
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <div className="card-dialog-label">Select Suit for {selectedRank}</div>
                  <div className="suit-picker">
                    {CARD_SUITS.map(suit => {
                      const isUsed = isCardUsed(selectedRank, suit.symbol);
                      return (
                        <button 
                          key={suit.symbol} 
                          className={`suit-button ${isUsed ? 'used' : ''}`}
                          onClick={() => !isUsed && handleSuitSelection(suit.symbol)}
                          aria-label={suit.label}
                          disabled={isUsed}
                        >
                          {suit.icon}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            
            <Dialog.Close asChild>
              <button className="card-dialog-close" aria-label="Close">
                <BsX />
              </button>
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  };

  const formatCard = (card: string) => {
    if (!card || card.length !== 2) return null;
    
    const rank = card[0];
    const suit = card[1];
    
    const suitIcon = CARD_SUITS.find(s => s.symbol === suit)?.icon;
    const isRed = suit === 'h' || suit === 'd';
    
    const suitSymbolMap: Record<string,string> = { h:'♥', d:'♦', c:'♣', s:'♠' };
    const suitSymbol = suitSymbolMap[suit] || suit;
    
    return (
      <span className={`history-card__content ${isRed ? 'history-card__content--red' : 'history-card__content--black'}`}> 
        <span className="history-card__rank">{rank.toUpperCase()}</span>
        <span className="history-card__suit">{suitSymbol}</span>
      </span>
    );
  };

  // NEW: Helpers for Results tab rendering
  const getCardPairFromSummary = (summaryJson: any): string[] => {
    const hc = summaryJson?.heroCards;
    if (!hc) return [];
    return [hc.holeCard1, hc.holeCard2].filter(Boolean);
  };
  const getBoardFromSummary = (summaryJson: any): string[] => {
    return Array.isArray(summaryJson?.board) ? summaryJson.board : [];
  };
  const renderResultsItem = (hand: SessionHandSummary) => {
    const summary = hand.summaryJson || {};
    const heroCards = getCardPairFromSummary(summary);
    const community = getBoardFromSummary(summary);
    const shownPot = hand.potSize ?? summary.finalPot ?? 'N/A';
    const rating = hand.overallRating ?? summary.overallRating ?? undefined;

    return (
      <div
        key={hand.handId}
        className="hand-note-item"
        onClick={() => {
          console.log('Opening analyzed hand in replayer:', hand.handId);
          navigate(`/hand-review/user/${PlayFab.getPlayerId()}/hand/${hand.handId}`, {
            state: {
              handSummary: hand.summaryJson || undefined,
              handId: hand.handId,
              ownerPlayfabId: PlayFab.getPlayerId(),
            },
          });
          setShowHistory(false);
        }}
        style={{ cursor: 'pointer' }}
      >
        <div className="hand-note-header" style={{ alignItems: 'center', gap: 8 }}>
          <span className="hand-note-position">
            {summary?.metadata?.handType || hand.handType || 'Hand'}
          </span>
          <span className="hand-note-cards" style={{ display: 'flex', gap: 6 }}>
            {heroCards.map((c, idx) => (
              <HandHistoryCard key={`${hand.handId}-hc-${idx}`} rank={c[0]} suit={c[1]} />
            ))}
          </span>
        </div>

        {community.length > 0 && (
          <div className="hand-note-board">
            <div className="hand-note-street" style={{ alignItems: 'center', gap: 6 }}>
              <span className="street-label">Board:</span>
              <span className="card-display-mini" style={{ display: 'flex', gap: 6 }}>
                {community.map((c, idx) => (
                  <HandHistoryCard key={`${hand.handId}-bd-${idx}`} rank={c[0]} suit={c[1]} />
                ))}
              </span>
            </div>
          </div>
        )}

        <div className="hand-note-timestamp">
          {hand.timestamp ? new Date(hand.timestamp).toLocaleString() : ''}
        </div>
        <div style={{ marginTop: 4, display: 'flex', gap: 12, fontSize: '0.85rem' }}>
          <span><strong>Pot</strong> {typeof shownPot === 'number' ? `$${shownPot}` : shownPot}</span>
          {typeof rating === 'number' && (
            <span><strong>Rating</strong> {rating.toFixed(1)}/10</span>
          )}
          {typeof hand.heroProfitBB === 'number' && (
            <span><strong>Result</strong> {hand.heroProfitBB > 0 ? '+' : ''}{hand.heroProfitBB.toFixed(2)} BB</span>
          )}
        </div>
      </div>
    );
  };

  const getHandNoteWithFoldIndicator = (note: any) => {
    // Check if any of the notes contain fold text
    const isFolded = 
      (!!note.preflopNotes && typeof note.preflopNotes === 'string' && note.preflopNotes.includes('Folded')) ||
      (!!note.flopNotes && typeof note.flopNotes === 'string' && note.flopNotes.includes('Folded')) ||
      (!!note.turnNotes && typeof note.turnNotes === 'string' && note.turnNotes.includes('Folded')) ||
      (!!note.riverNotes && typeof note.riverNotes === 'string' && note.riverNotes.includes('Folded'));
    
    return (
      <div 
        key={note.handId || note.timestamp} 
        className={`hand-note-item ${isFolded ? 'folded-hand' : ''}`}
        onClick={() => handleSelectHandNote(note)}
      >
        <div className="hand-note-header">
          <span className="hand-note-position">
            {isFolded && (
              <span className="fold-indicator" title="Hand was folded">
                <BsX style={{ color: 'red', marginRight: '4px' }} />
              </span>
            )}
            {note.position || 'Position: ?'}
          </span>
          <span className="hand-note-cards">
            {note.heroCards ? (
              <span className="card-display-mini">
                {note.heroCards.substring(0,2) && note.heroCards.substring(0,2).length===2 && (
                  <HandHistoryCard rank={note.heroCards.substring(0,2)[0]} suit={note.heroCards.substring(0,2)[1]} />
                )}
                {note.heroCards.substring(2,4) && note.heroCards.substring(2,4).length===2 && (
                  <HandHistoryCard rank={note.heroCards.substring(2,4)[0]} suit={note.heroCards.substring(2,4)[1]} />
                )}
              </span>
            ) : 'Cards: ?'}
          </span>
        </div>
        
        {/* Board cards single-line */}
        {(note.flopCards || note.turnCard || note.riverCard) && (
          <div className="hand-note-board">
            <div className="hand-note-street">
              <span className="street-label">Board:</span>
              <span className="card-display-mini" style={{ display:'flex', gap: 6 }}>
                {note.flopCards && note.flopCards.length>=2 && (
                  <HandHistoryCard rank={note.flopCards.substring(0,2)[0]} suit={note.flopCards.substring(0,2)[1]} />
                )}
                {note.flopCards && note.flopCards.length>=4 && (
                  <HandHistoryCard rank={note.flopCards.substring(2,4)[0]} suit={note.flopCards.substring(2,4)[1]} />
                )}
                {note.flopCards && note.flopCards.length>=6 && (
                  <HandHistoryCard rank={note.flopCards.substring(4,6)[0]} suit={note.flopCards.substring(4,6)[1]} />
                )}
                {note.turnCard && note.turnCard.length===2 && (
                  <HandHistoryCard rank={note.turnCard[0]} suit={note.turnCard[1]} />
                )}
                {note.riverCard && note.riverCard.length===2 && (
                  <HandHistoryCard rank={note.riverCard[0]} suit={note.riverCard[1]} />
                )}
              </span>
            </div>
          </div>
        )}
        
        <div className="hand-note-timestamp">
          {new Date(note.timestamp).toLocaleString()}
        </div>
      </div>
    );
  };

  // Function to check if a street was folded
  const wasStreetFolded = (street: StreetTab): boolean => {
    switch (street) {
      case 'preflop':
        return !!handNote.preflopNotes && typeof handNote.preflopNotes === 'string' && handNote.preflopNotes.includes('Folded preflop');
      case 'flop':
        return !!handNote.flopNotes && typeof handNote.flopNotes === 'string' && handNote.flopNotes.includes('Folded on flop');
      case 'turn':
        return !!handNote.turnNotes && typeof handNote.turnNotes === 'string' && handNote.turnNotes.includes('Folded on turn');
      case 'river':
        return !!handNote.riverNotes && typeof handNote.riverNotes === 'string' && handNote.riverNotes.includes('Folded on river');
      default:
        return false;
    }
  };

  // ADDED: Handler for converting hand note to structured hand history
  const handleConvertToHistory = async () => {
    setIsConverting(true);
    setConversionError(null);
    setConvertedHandHistory(null); // Clear any previous conversion result

    try {
      if (!handNote.position || !handNote.heroCards || !handNote.smallBlind || !handNote.bigBlind) {
        const errorMsg = "Position, Hero Cards, and Blinds are required for conversion.";
        setConversionError(errorMsg);
        alert(errorMsg); // Keep alert for now, can be replaced with better UI notification
        setIsConverting(false);
        return;
      }

      console.log("Attempting to trigger asynchronous conversion for hand note:", handNote);

      const result = await PlayFab.executeCloudScript({
        FunctionName: "convertHandNoteToHistory",
        FunctionParameter: {
          handNote: handNote,
          playfabId: PlayFab.getPlayerId(), 
          sessionId: handNote.sessionId || "default_session" 
        }
      });

      console.log('CloudScript convertHandNoteToHistory trigger result:', result);

      // If the overall CloudScript execution was successful (result.success is true),
      // consider the trigger successful for a "fire-and-forget" operation.
      if (result.success) {
        const handIdFromServer = result.data?.FunctionResult?.handId || handNote.handId || "N/A";
        alert('Conversion process initiated successfully! The hand history will be available shortly.'); 
        console.log("Hand note conversion process initiated via CloudScript. Hand ID:", handIdFromServer);
      } else {
        // Error in CloudScript execution itself, or it explicitly returned an error structure.
        const errorMessage = result.data?.FunctionResult?.error || 
                             result.data?.Error?.errorMessage || 
                             result.data?.errorMessage || 
                             "Failed to initiate hand note conversion on the server.";
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error('Error initiating hand note conversion:', error);
      const message = error.message || 'An unknown error occurred while starting the conversion.';
      setConversionError(message);
      alert(`Conversion initiation failed: ${message}`);
    } finally {
      setIsConverting(false);
    }
  };

  // Cancel (undo) last action for a given player
  const handleCancelAction = (playerId: number) => {
    const player = players.find(p => p.id === playerId);
    if (!player) return;
    const playerName = player.position;

    // Remove last action in handNote.actions for this player
    setHandNote(prev => {
      if (!prev.actions) return prev;
      const idx = [...prev.actions].reverse().findIndex(a => a.playerName === playerName && a.street === activeTab);
      if (idx === -1) return prev;
      const removeIndex = prev.actions.length - 1 - idx;
      const newActions = [...prev.actions];
      const removed = newActions.splice(removeIndex, 1)[0];

      // Build new lastActions map BEFORE setting state so Seat rerenders immediately
      setLastActions(prevMap => {
        const list = [...(prevMap[playerId] || [])];
        list.pop();
        return { ...prevMap, [playerId]: list };
      });

      // Recalculate currentBet if needed
      if (removed.action === 'straddle') {
        // Decrement straddle count (but not below 0)
        setStraddleCount(prev => (prev > 0 ? prev - 1 : 0));

        // Determine the new live blind after removing this straddle
        const prevStraddles = newActions.filter(
          a => a.street === 'preflop' && a.action === 'straddle'
        );
        if (prevStraddles.length > 0) {
          setCurrentBet(prevStraddles[prevStraddles.length - 1].amount || 0);
        } else {
          // Fall back to last raise amount or 0
          const raises = newActions.filter(
            a => a.street === activeTab && a.action === 'raise'
          );
          const newBet = raises.length > 0 ? raises[raises.length - 1].amount || 0 : 0;
          setCurrentBet(newBet);
        }
      } else if (removed.action === 'raise') {
        const raises = newActions.filter(a => a.street === activeTab && a.action === 'raise');
        const newBet = raises.length > 0 ? raises[raises.length - 1].amount || 0 : 0;
        setCurrentBet(newBet);
      }
      return { ...prev, actions: newActions };
    });
  };

  // ====== Hand strength evaluation ======
  useEffect(() => {
    const heroCardsArr: string[] = [];
    if (handNote.heroCards.length >= 2) heroCardsArr.push(handNote.heroCards.substring(0, 2));
    if (handNote.heroCards.length >= 4) heroCardsArr.push(handNote.heroCards.substring(2, 4));

    const boardArr: string[] = [];
    if (['flop', 'turn', 'river', 'showdown'].includes(activeTab)) {
      if (handNote.flopCards.length >= 6) {
        boardArr.push(
          handNote.flopCards.substring(0, 2),
          handNote.flopCards.substring(2, 4),
          handNote.flopCards.substring(4, 6)
        );
      }
    }
    if (['turn', 'river', 'showdown'].includes(activeTab)) {
      if (handNote.turnCard.length === 2) boardArr.push(handNote.turnCard);
    }
    if (['river', 'showdown'].includes(activeTab)) {
      if (handNote.riverCard.length === 2) boardArr.push(handNote.riverCard);
    }

    const evalRes = evaluateTexasHoldemHand(heroCardsArr, boardArr);
    let display = evalRes.description;
    if (evalRes.outs && evalRes.equityPct !== undefined && boardArr.length > 0) {
      display += ` | ${evalRes.outs} outs (${evalRes.equityPct}%)`;
    }
    setHandEval(display);
  }, [handNote.heroCards, handNote.flopCards, handNote.turnCard, handNote.riverCard, activeTab]);

  // Straddle helpers
  const activePlayerActionsCount =
    activePlayerId !== null && lastActions[activePlayerId]
      ? lastActions[activePlayerId].length
      : 0;
  const activePlayerObj = players.find(p => p.id === activePlayerId);
  const activePosition = activePlayerObj?.position.toLowerCase();
  // Straddle eligibility: preflop, player hasn't acted, and their position index equals current straddleCount (UTG first, then UTG+1, etc.)
  const positionIndex = PREFLOP_ORDER.findIndex(pos => pos === activePosition);
  const canStraddle =
    activeTab === 'preflop' &&
    activePlayerActionsCount === 0 &&
    positionIndex === straddleCount &&
    positionIndex !== -1;
  const straddleLabel = 'Straddle';

  return (
    <div className="page">
      <div className="hand-notes-container">
        <form onSubmit={handleSubmit} className="hand-notes-form">
          <div className="form-header">
            <div className="session-info-display">
              <div className="header-buttons">
                <button
                  type="button"
                  className="settings-button"
                  onClick={async ()=>{
                    // Save Draft handler
                    if (!handNote.sessionId) { alert('Please set a Session ID before saving.'); return; }
                    setIsSavingDraft(true);
                    try {
                      console.log('Saving draft. actions length:', (handNote.actions||[]).length, 'sample:', (handNote.actions||[]).slice(0,5));
                      // Ensure blinds are included if no actions yet
                      const sbAmt = parseFloat(handNote.smallBlind) || 0;
                      const bbAmt = parseFloat(handNote.bigBlind) || 0;
                      const ensuredActions = (handNote.actions && handNote.actions.length > 0)
                        ? handNote.actions
                        : (sbAmt > 0 && bbAmt > 0
                            ? [
                                { playerName: 'SB', action: 'posts', amount: sbAmt, street: 'preflop' },
                                { playerName: 'BB', action: 'posts', amount: bbAmt, street: 'preflop' },
                              ]
                            : []);
                      const payload = {
                        sessionId: handNote.sessionId,
                        smallBlind: handNote.smallBlind,
                        bigBlind: handNote.bigBlind,
                        numberOfPlayers: handNote.numberOfPlayers,
                        position: handNote.position,
                        heroCards: handNote.heroCards,
                        preflopNotes: handNote.preflopNotes,
                        flopCards: handNote.flopCards,
                        flopNotes: handNote.flopNotes,
                        turnCard: handNote.turnCard,
                        turnNotes: handNote.turnNotes,
                        riverCard: handNote.riverCard,
                        riverNotes: handNote.riverNotes,
                        showdownNotes: handNote.showdownNotes,
                        handId: handNote.handId || generateHandId(),
                        timestamp: new Date().toISOString(),
                        // Extended fields
                        actions: ensuredActions,
                        potSize: potSize,
                        players: players,
                        notesVersion: 1,
                      };

                      const result = await PlayFab.executeCloudScript({
                        FunctionName: 'saveHandNote',
                        FunctionParameter: {
                          handNote: payload,
                          action: 'draft',
                          isUpdate: isEditing,
                        },
                      });
                      console.log('Save draft CloudScript result:', result);

                      if (!result.success) throw new Error('CloudScript failed');
                      alert('Saved!');
                      // Mark as editing so future saves are updates
                      setIsEditing(true);
                    } catch (err:any) {
                      alert('Save failed: ' + (err?.message || 'Unknown error'));
                    } finally {
                      setIsSavingDraft(false);
                    }
                  }}
                  aria-label="Save Draft"
                  title="Save Draft"
                  disabled={isSavingDraft || isSubmitting}
                >
                  {isSavingDraft ? '...' : <BsFloppyFill />}
                </button>
                <button 
                  type="button" 
                  className="history-button" 
                  onClick={toggleHistory}
                  aria-label="Hand Notes History"
                >
                  <BsClock />
                </button>
                <button
                  type="button"
                  className="timeline-button"
                  onClick={() => setShowTimeline(true)}
                  aria-label="Action Timeline"
                  title="Action Timeline"
                >
                  <BsThreeDots />
                </button>
                <button
                  type="button"
                  className="settings-button"
                  onClick={() => setNotesModal('hero')}
                  aria-label="Edit Notes"
                  title="Edit Notes"
                >
                  <BsStickyFill />
                </button>
                {activeTab === 'preflop' && (
                  <button
                    type="button"
                    className="settings-button"
                    onClick={handleFoldClick}
                    aria-label="Quick Fold Preflop"
                    title="Quick Fold Preflop"
                  >
                    <BsBackspace />
                  </button>
                )}
                <button 
                  type="button" 
                  className="settings-button" 
                  onClick={toggleSettings}
                  aria-label="Session Settings"
                >
                  <BsGear />
                </button>
                <button
                  type="submit"
                  className="submit-header-button"
                  disabled={isSubmitting}
                  style={{marginLeft:8}}
                >
                  {isSubmitting ? 'Submitting…' : 'Submit'}
                </button>
              </div>
            </div>
            
            <div className="position-hero-container">
              <div className="position-stack-row" style={{display:'flex',gap:12,flexWrap:'nowrap',alignItems:'flex-end'}}> 
              <div className="form-group position-selector">
                <label htmlFor="position">Hero Position</label>
                <div className="select-wrapper">
                  <Select.Root value={handNote.position} onValueChange={handlePositionChange}>
                    <Select.Trigger className="select-trigger" aria-label="Position">
                      <Select.Value placeholder="Select position" />
                      <Select.Icon className="select-icon">
                        <BsChevronDown />
                      </Select.Icon>
                    </Select.Trigger>
                    <Select.Portal>
                      <Select.Content className="select-content" position="popper" sideOffset={4} avoidCollisions={true}>
                        <Select.Viewport className="select-viewport">
                          <Select.Group>
                            {getAvailablePositions().map((pos) => (
                              <Select.Item key={pos.value} value={pos.value} className="select-item">
                                <Select.ItemText>{pos.label}</Select.ItemText>
                              </Select.Item>
                            ))}
                          </Select.Group>
                        </Select.Viewport>
                      </Select.Content>
                    </Select.Portal>
                  </Select.Root>
                </div>
              </div>

              {/* Stack Size Input */}
              <div className="form-group stack-size-input" style={{maxWidth:120}}>
                <label htmlFor="stackSize">{`${players.find(p=>p.id===activePlayerId)?.position?.toUpperCase() || ''} Stack ($)`}</label>
                <input
                  id="stackSize"
                  type="number"
                  min="0"
                  value={(players.find(p=>p.id===activePlayerId)?.stackSize) ?? ''}
                  onChange={e=>{
                    const val=parseInt(e.target.value,10)||0;
                    setPlayers(prev=>prev.map(p=>p.id===activePlayerId?{...p,stackSize:val}:p));
                  }}
                  onFocus={(e)=>{
                    e.preventDefault();
                    e.currentTarget.blur();
                    setIsStackPopupOpen(true);
                  }}
                />
              </div>
              </div>
            
              <div className="form-group hero-cards-input hero-cards-section">
                <label htmlFor="heroCards">Hero Cards</label>
                {getHeroCardDisplay()}
                <div className="hand-strength-display" style={{ marginTop: 4 }}>
                  {handEval
                    ? handEval.split('|').map((p, i) => {
                        const idx = p.indexOf('(');
                        if (idx > -1) {
                          const main = p.slice(0, idx).trim();
                          const bracket = p.slice(idx).trim();
                          return (
                            <div key={i}>
                              <div>{main}</div>
                              <div>{bracket}</div>
                            </div>
                          );
                        }
                        return <div key={i}>{p.trim()}</div>;
                      })
                    : 'Hand: -'}
                </div>
                {/* Quick fold shown only preflop */}
                
              

            </div>
             </div>
          </div>
          
          {/* Settings Popup */}
          {showSettings && (
            <div className="settings-modal">
              <div className="settings-content">
                <h3>Session Settings</h3>
                <div className="form-group">
                  <label htmlFor="sessionId">Session ID</label>
                  <input
                    id="sessionId"
                    name="sessionId"
                    value={handNote.sessionId}
                    onChange={handleInputChange}
                    placeholder="Enter session ID"
                    required
                  />
                </div>
                
                <div className="blinds-container">
                  <div className="form-group">
                    <label htmlFor="smallBlind">Small Blind</label>
                    <input
                      id="smallBlind"
                      name="smallBlind"
                      type="number"
                      min="0"
                      value={handNote.smallBlind}
                      onChange={handleInputChange}
                      placeholder="e.g., 1"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="bigBlind">Big Blind</label>
                    <input
                      id="bigBlind"
                      name="bigBlind"
                      type="number"
                      min="0"
                      value={handNote.bigBlind}
                      onChange={handleInputChange}
                      placeholder="e.g., 2"
                      required
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="numberOfPlayers">Number of Players</label>
                  <div className="select-wrapper">
                    <Select.Root value={handNote.numberOfPlayers} onValueChange={handlePlayerCountChange}>
                      <Select.Trigger className="select-trigger" aria-label="Number of Players">
                        <Select.Value placeholder="Select player count" />
                        <Select.Icon className="select-icon">
                          <BsChevronDown />
                        </Select.Icon>
                      </Select.Trigger>
                      
                      <Select.Portal>
                        <Select.Content className="select-content" position="popper" sideOffset={4} avoidCollisions={true}>
                          <Select.Viewport className="select-viewport">
                            <Select.Group>
                              {playerCounts.map((option) => (
                                <Select.Item 
                                  key={option.value} 
                                  value={option.value}
                                  className="select-item"
                                >
                                  <Select.ItemText>{option.label}</Select.ItemText>
                                </Select.Item>
                              ))}
                            </Select.Group>
                          </Select.Viewport>
                        </Select.Content>
                      </Select.Portal>
                    </Select.Root>
                  </div>
                </div>
                
                <div className="settings-actions">
                  <button 
                    type="button" 
                    className="save-button" 
                    onClick={()=>{
                      setIsTemplateModalOpen(true);
                      setTemplateText('');
                    }}
                    style={{marginRight:8, color:'#fff'}}
                  >
                    Load Hand Template
                  </button>
                  <button 
                    type="button" 
                    className="save-button" 
                    onClick={openViewTemplate}
                    style={{marginRight:8, color:'#fff'}}
                  >
                    View Hand Template
                  </button>
                  <button 
                    type="button" 
                    className="save-button" 
                    onClick={loadLastHandFromLocal}
                    style={{marginRight:8, color:'#fff'}}
                  >
                    Load Last Hand
                  </button>
                  <button 
                    type="button" 
                    className="save-button" 
                    onClick={closeSettings}
                    style={{color:'#fff'}}
                  >
                    Save
                  </button>
                </div>
              </div>
              <div className="settings-backdrop" onClick={closeSettings}></div>
            </div>
          )}
          {/* Template paste modal */}
          <Dialog.Root open={isTemplateModalOpen} onOpenChange={setIsTemplateModalOpen}>
            <Dialog.Portal>
              <Dialog.Overlay className="card-dialog-overlay" />
              <Dialog.Content className="card-dialog-content" style={{ maxWidth: 560, width: '95%' }}>
                <Dialog.Title className="card-dialog-title">Load Hand Template (Paste JSON)</Dialog.Title>
                <textarea
                  value={templateText}
                  onChange={e=>setTemplateText(e.target.value)}
                  placeholder='Paste a JSON containing { handNote, players, potSize, summary } or a summary JSON'
                  style={{ width: '100%', minHeight: 220, fontFamily: 'monospace' }}
                />
                <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:8 }}>
                  <button
                    className="save-button"
                    type="button"
                    onClick={()=>{
                      try{
                        const obj = JSON.parse(templateText);
                        applyTemplateObject(obj);
                        setIsTemplateModalOpen(false);
                        alert('Template loaded.');
                      }catch(err){
                        alert('Invalid JSON.');
                      }
                    }}
                  >
                    Apply
                  </button>
                  <Dialog.Close asChild>
                    <button className="save-button" type="button">Close</button>
                  </Dialog.Close>
                </div>
                <Dialog.Close asChild>
                  <button className="card-dialog-close" aria-label="Close"><BsX/></button>
                </Dialog.Close>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
          {/* View template modal */}
          <Dialog.Root open={isViewTemplateOpen} onOpenChange={setIsViewTemplateOpen}>
            <Dialog.Portal>
              <Dialog.Overlay className="card-dialog-overlay" />
              <Dialog.Content className="card-dialog-content" style={{ maxWidth: 720, width: '95%' }}>
                <Dialog.Title className="card-dialog-title">Current Hand Template (LLM Summary JSON)</Dialog.Title>
                <textarea
                  readOnly
                  value={viewTemplateText}
                  style={{ width: '100%', minHeight: 340, fontFamily: 'monospace' }}
                />
                <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:8 }}>
                  <button
                    className="save-button"
                    type="button"
                    onClick={()=>{
                      navigator.clipboard?.writeText(viewTemplateText).catch(()=>{});
                      setIsViewTemplateOpen(false);
                    }}
                  >
                    Copy & Close
                  </button>
                  <Dialog.Close asChild>
                    <button className="save-button" type="button">Close</button>
                  </Dialog.Close>
                </div>
                <Dialog.Close asChild>
                  <button className="card-dialog-close" aria-label="Close"><BsX/></button>
                </Dialog.Close>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
          
          {/* History Popup */}
          {showHistory && (
            <div className="settings-modal">
              <div className="settings-content history-content">
                <h3>
                  Hand Notes History <span style={{ color: '#FFCE3E', marginLeft: 6 }}>★</span>
                </h3>
                <button
                  type="button"
                  className="modal-close-x"
                  aria-label="Close"
                  onClick={closeHistory}
                >
                  <BsX />
                </button>

                {/* Compact session selector row */}
                <div className="history-session-row">
                  <label style={{ fontWeight: 700 }}>Session</label>
                  <div className="select-wrapper" style={{ flex: 1 }}>
                    <Select.Root
                      value={historySessionId}
                      onOpenChange={(open)=>{
                        // no-op; placeholder if needed later
                      }}
                      onValueChange={(sid) => {
                        const trimmed = (sid || '').trim();
                        // Update state first, then trigger load in a microtask to ensure state is applied
                        setHistorySessionId(trimmed);
                        if (!trimmed) return;
                        queueMicrotask(() => {
                          if (historyTab === 'drafts') {
                            handleLoadHistory(trimmed);
                          } else {
                            handleLoadResults(trimmed);
                          }
                        });
                      }}
                    >
                      <Select.Trigger className="select-trigger" aria-label="Session">
                        <Select.Value placeholder={isLoadingSessions ? 'Loading sessions…' : 'Select a session'} />
                        <Select.Icon className="select-icon">
                          <BsChevronDown />
                        </Select.Icon>
                      </Select.Trigger>
                      <Select.Portal>
                        <Select.Content className="select-content" position="popper" sideOffset={4} avoidCollisions>
                          <Select.Viewport className="select-viewport">
                            <Select.Group>
                              {(sessions || []).length === 0 ? (
                                <div style={{ padding: 8, color: 'var(--subtext-color)' }}>No sessions</div>
                              ) : (
                                sessions.map((sid) => (
                                  <Select.Item key={sid} value={sid} className="select-item">
                                    <Select.ItemText>{sid}</Select.ItemText>
                                  </Select.Item>
                                ))
                              )}
                            </Select.Group>
                          </Select.Viewport>
                        </Select.Content>
                      </Select.Portal>
                    </Select.Root>
                  </div>
                  <button
                    type="button"
                    className="search-button"
                    onClick={handleLoadSessions}
                    disabled={isLoadingSessions}
                    title="Refresh sessions"
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {isLoadingSessions ? '...' : 'Refresh'}
                  </button>
                </div>

                <Tabs.Root
                  value={historyTab}
                  onValueChange={(v) => {
                    setHistoryTab(v as HistoryTab);
                    const sid = (historySessionId || '').trim();
                    if (!sid) return;
                    if (v === 'drafts') {
                      handleLoadHistory(sid);
                    } else if (v === 'results') {
                      handleLoadResults(sid);
                    }
                  }}
                  className="tabs-root"
                >
                  <Tabs.List className="tabs-list">
                    <Tabs.Trigger value="drafts" className="tabs-trigger">Drafts</Tabs.Trigger>
                    <Tabs.Trigger value="results" className="tabs-trigger">Analysis</Tabs.Trigger>
                  </Tabs.List>

                  {/* Drafts Tab */}
                  <Tabs.Content value="drafts" className="tabs-content" data-state={historyTab === 'drafts' ? 'active' : undefined}>

                    {handNotes.length > 0 && (
                      <div className="hand-notes-list">
                        <h4>Found {handNotes.length} draft notes</h4>
                        <div className="scrollable-list">
                          {handNotes.map(note => getHandNoteWithFoldIndicator(note))}
                        </div>
                      </div>
                    )}
                  </Tabs.Content>

                  {/* Results Tab */}
                  <Tabs.Content value="results" className="tabs-content" data-state={historyTab === 'results' ? 'active' : undefined}>

                    {resultsError && <div className="hand-history__error">{resultsError}</div>}
                    {resultsHands.length > 0 && (
                      <div className="hand-notes-list">
                        <h4>Found {resultsHands.length} analyzed hands</h4>
                        <div className="scrollable-list">
                          {resultsHands.map(h => renderResultsItem(h))}
                        </div>
                      </div>
                    )}
                  </Tabs.Content>
                </Tabs.Root>

                {/* Close button moved to top-right X */}
              </div>
              <div className="settings-backdrop" onClick={closeHistory}></div>
            </div>
          )}
          
          {/* Tabs Navigation using Radix UI */}
          <Tabs.Root value={activeTab} onValueChange={handleTabChange} className="tabs-root">
            <Tabs.List className="tabs-list">
              <Tabs.Trigger value="preflop" className={`tabs-trigger ${wasStreetFolded('preflop') ? 'folded-street' : ''}`} title="Preflop">
                Preflop
                {wasStreetFolded('preflop') && <BsX className="tab-fold-indicator" />}
              </Tabs.Trigger>
              <Tabs.Trigger value="flop" className={`tabs-trigger ${wasStreetFolded('flop') ? 'folded-street' : ''}`} title="Flop">
                Flop
                {wasStreetFolded('flop') && <BsX className="tab-fold-indicator" />}
              </Tabs.Trigger>
              <Tabs.Trigger value="turn" className={`tabs-trigger ${wasStreetFolded('turn') ? 'folded-street' : ''}`} title="Turn">
                Turn
                {wasStreetFolded('turn') && <BsX className="tab-fold-indicator" />}
              </Tabs.Trigger>
              <Tabs.Trigger value="river" className={`tabs-trigger ${wasStreetFolded('river') ? 'folded-street' : ''}`} title="River">
                River
                {wasStreetFolded('river') && <BsX className="tab-fold-indicator" />}
              </Tabs.Trigger>
              <Tabs.Trigger value="showdown" className="tabs-trigger" title="Showdown">
                Showdown
              </Tabs.Trigger>
            </Tabs.List>

            {/* Tab Content */}
            <div className="tab-content">
              {/* Preflop Tab Content */}
              <Tabs.Content value="preflop" className="tabs-content">
                <div className="tab-pane">

                  <div className="form-group">
                    <label htmlFor="board">Board</label>
                    <div className="board-actions-container">
                      <div className="board-row">
                        <div className="board-container">
                          {getBoardCardDisplay()}
                          {/* Removed TableActionOverlay */}
                        </div>
                        <div className="pot-container">
                          <div className="pot-label">Pot</div>
                          <div className="pot-amount">${potSize.toFixed(2)}</div>
                        </div>
                        <div className="notes-top-right">
                          <button
                            type="button"
                            className="notes-icon-button"
                            onClick={() => setNotesModal('preflop')}
                            title="Edit Preflop Notes"
                          >
                            <BsStickyFill />
                            {handNote.preflopNotes ? <span style={{marginLeft: 4, color: 'var(--primary-color)'}}>•</span> : null}
                          </button>
                          <NotesDialog
                            open={notesModal === 'preflop'}
                            onOpenChange={open => setNotesModal(open ? 'preflop' : null)}
                            value={handNote.preflopNotes}
                            onChange={val => setHandNote(prev => ({ ...prev, preflopNotes: val }))}
                            label="Preflop Notes"
                          />
                        </div>
                      </div>
                      <div className="inline-actions">
                        <button
                          type="button"
                          className="convert-button"
                          onClick={handleConvertToHistory}
                          disabled={isSubmitting || isConverting}
                          title="Convert to structured Hand History via AI"
                        >
                          {isConverting ? 'Converting...' : <BsSend />}
                        </button>
                        <button 
                          type="submit" 
                          className="submit-button" 
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? 'Submitting...' : 'Submit'}
                        </button>
                      </div>
                    </div>
                    {renderBoardContextLine()}
                  </div>
                  {/* ActionBar placeholder - preflop */}
                  <ActionBar
                    callCheckLabel={callCheckLabel}
                    onCall={() => handleAction('call')}
                    onRaise={() => handleAction('raise')}
                    onFold={() => handleAction('fold')}
                    canStraddle={canStraddle}
                    straddleLabel={straddleLabel}
                    onStraddle={() => handleAction('straddle')}
                  />
                  <HandNotesTable 
                    players={players} 
                    onPlayerClick={setActivePlayerId} 
                    currentTurnPlayerId={activePlayerId} 
                    lastActions={lastActions} 
                    heroPosition={handNote.position}
                    onCancelAction={handleCancelAction}
                    board={getBoardCardDisplay()}
                    potSize={potSize}
                  />
                </div>
              </Tabs.Content>
              
              {/* Flop Tab Content */}
              <Tabs.Content value="flop" className="tabs-content">
                <div className="tab-pane">
                  <div className="form-group">
                    <label htmlFor="board">Board</label>
                    <div className="board-actions-container">
                      <div className="board-row">
                        <div className="board-container">
                          {getBoardCardDisplay()}
                          {/* Removed TableActionOverlay */}
                        </div>
                        <div className="pot-container">
                          <div className="pot-label">Pot</div>
                          <div className="pot-amount">${potSize.toFixed(2)}</div>
                        </div>
                        <div className="notes-top-right">
                          <button
                            type="button"
                            className="notes-icon-button"
                            onClick={() => setNotesModal('flop')}
                            title="Edit Flop Notes"
                          >
                            <BsStickyFill />
                            {handNote.flopNotes ? <span style={{marginLeft: 4, color: 'var(--primary-color)'}}>•</span> : null}
                          </button>
                          <NotesDialog
                            open={notesModal === 'flop'}
                            onOpenChange={open => setNotesModal(open ? 'flop' : null)}
                            value={handNote.flopNotes}
                            onChange={val => setHandNote(prev => ({ ...prev, flopNotes: val }))}
                            label="Flop Notes"
                          />
                        </div>
                      </div>
                      <div className="inline-actions">
                        <button
                          type="button"
                          className="convert-button"
                          onClick={handleConvertToHistory}
                          disabled={isSubmitting || isConverting}
                          title="Convert to structured Hand History via AI"
                        >
                          {isConverting ? 'Converting...' : <BsSend />}
                        </button>
                        <button 
                          type="submit" 
                          className="submit-button" 
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? 'Submitting...' : 'Submit'}
                        </button>
                      </div>
                    </div>
                  </div>
                  {/* ActionBar placeholder - flop */}
                  <ActionBar
                    callCheckLabel={callCheckLabel}
                    onCall={() => handleAction('call')}
                    onRaise={() => handleAction('raise')}
                    onFold={() => handleAction('fold')}
                    canStraddle={canStraddle}
                    straddleLabel={straddleLabel}
                    onStraddle={() => handleAction('straddle')}
                  />
                  <HandNotesTable 
                    players={players} 
                    onPlayerClick={setActivePlayerId} 
                    currentTurnPlayerId={activePlayerId} 
                    lastActions={lastActions} 
                    heroPosition={handNote.position}
                    onCancelAction={handleCancelAction}
                    board={getBoardCardDisplay()}
                    potSize={potSize}
                  />
                </div>
              </Tabs.Content>
              
              {/* Turn Tab Content */}
              <Tabs.Content value="turn" className="tabs-content">
                <div className="tab-pane">
                  <div className="form-group">
                    <label htmlFor="board">Board</label>
                    <div className="board-actions-container">
                      <div className="board-row">
                        <div className="board-container">
                          {getBoardCardDisplay()}
                          {/* Removed TableActionOverlay */}
                        </div>
                        <div className="pot-container">
                          <div className="pot-label">Pot</div>
                          <div className="pot-amount">${potSize.toFixed(2)}</div>
                        </div>
                        <div className="notes-top-right">
                          <button
                            type="button"
                            className="notes-icon-button"
                            onClick={() => setNotesModal('turn')}
                            title="Edit Turn Notes"
                          >
                            <BsStickyFill />
                            {handNote.turnNotes ? <span style={{marginLeft: 4, color: 'var(--primary-color)'}}>•</span> : null}
                          </button>
                          <NotesDialog
                            open={notesModal === 'turn'}
                            onOpenChange={open => setNotesModal(open ? 'turn' : null)}
                            value={handNote.turnNotes}
                            onChange={val => setHandNote(prev => ({ ...prev, turnNotes: val }))}
                            label="Turn Notes"
                          />
                        </div>
                      </div>
                      <div className="inline-actions">
                        <button
                          type="button"
                          className="convert-button"
                          onClick={handleConvertToHistory}
                          disabled={isSubmitting || isConverting}
                          title="Convert to structured Hand History via AI"
                        >
                          {isConverting ? 'Converting...' : <BsSend />}
                        </button>
                        <button 
                          type="submit" 
                          className="submit-button" 
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? 'Submitting...' : 'Submit'}
                        </button>
                      </div>
                    </div>
                  </div>
                  {/* ActionBar placeholder - turn */}
                  <ActionBar
                    callCheckLabel={callCheckLabel}
                    onCall={() => handleAction('call')}
                    onRaise={() => handleAction('raise')}
                    onFold={() => handleAction('fold')}
                    canStraddle={canStraddle}
                    straddleLabel={straddleLabel}
                    onStraddle={() => handleAction('straddle')}
                  />
                  <HandNotesTable 
                    players={players} 
                    onPlayerClick={setActivePlayerId} 
                    currentTurnPlayerId={activePlayerId} 
                    lastActions={lastActions} 
                    heroPosition={handNote.position}
                    onCancelAction={handleCancelAction}
                    board={getBoardCardDisplay()}
                    potSize={potSize}
                  />
                </div>
              </Tabs.Content>
              
              {/* River Tab Content */}
              <Tabs.Content value="river" className="tabs-content">
                <div className="tab-pane">
                  <div className="form-group">
                    <label htmlFor="board">Board</label>
                    <div className="board-actions-container">
                      <div className="board-row">
                        <div className="board-container">
                          {getBoardCardDisplay()}
                          {/* Removed TableActionOverlay */}
                        </div>
                        <div className="pot-container">
                          <div className="pot-label">Pot</div>
                          <div className="pot-amount">${potSize.toFixed(2)}</div>
                        </div>
                        <div className="notes-top-right">
                          <button
                            type="button"
                            className="notes-icon-button"
                            onClick={() => setNotesModal('river')}
                            title="Edit River Notes"
                          >
                            <BsStickyFill />
                            {handNote.riverNotes ? <span style={{marginLeft: 4, color: 'var(--primary-color)'}}>•</span> : null}
                          </button>
                          <NotesDialog
                            open={notesModal === 'river'}
                            onOpenChange={open => setNotesModal(open ? 'river' : null)}
                            value={handNote.riverNotes}
                            onChange={val => setHandNote(prev => ({ ...prev, riverNotes: val }))}
                            label="River Notes"
                          />
                        </div>
                      </div>
                      <div className="inline-actions">
                        <button
                          type="button"
                          className="convert-button"
                          onClick={handleConvertToHistory}
                          disabled={isSubmitting || isConverting}
                          title="Convert to structured Hand History via AI"
                        >
                          {isConverting ? 'Converting...' : <BsSend />}
                        </button>
                        <button 
                          type="submit" 
                          className="submit-button" 
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? 'Submitting...' : 'Submit'}
                        </button>
                      </div>
                    </div>
                  </div>
                  {/* ActionBar placeholder - river */}
                  <ActionBar
                    callCheckLabel={callCheckLabel}
                    onCall={() => handleAction('call')}
                    onRaise={() => handleAction('raise')}
                    onFold={() => handleAction('fold')}
                    canStraddle={canStraddle}
                    straddleLabel={straddleLabel}
                    onStraddle={() => handleAction('straddle')}
                  />
                  <HandNotesTable 
                    players={players} 
                    onPlayerClick={setActivePlayerId} 
                    currentTurnPlayerId={activePlayerId} 
                    lastActions={lastActions} 
                    heroPosition={handNote.position}
                    onCancelAction={handleCancelAction}
                    board={getBoardCardDisplay()}
                    potSize={potSize}
                  />
                </div>
              </Tabs.Content>
              
              {/* Showdown Tab Content */}
              <Tabs.Content value="showdown" className="tabs-content showdown-tab">
                <div className="tab-pane">
                  <div className="form-group">
                    <label htmlFor="board">Board</label>
                    <div className="board-actions-container">
                      <div className="board-row">
                        <div className="board-container">
                          {getBoardCardDisplay()}
                        </div>
                        <div className="pot-container">
                          <div className="pot-label">Pot</div>
                          <div className="pot-amount">${potSize.toFixed(2)}</div>
                        </div>
                        {/* Notes button hidden on showdown */}
                      </div>
                    </div>
                  </div>

                  {/* Showdown player reveals (active players only) */}
                  <div className="showdown-players-row" style={{ marginTop: 8 }}>
                    {players.filter(p=>p.isActive).map(p=>{
                      const key = (p.position||'').toLowerCase();
                      const val = showdownCardsByPlayer[key] || '';
                      const first = val.substring(0,2);
                      const second = val.substring(2,4);
                      const mucked = !!showdownMuckByPlayer[key];
                      const isWinner = showdownWinners.has(key);
                      const isHero = (p.position||'').toLowerCase() === (handNote.position||'').toLowerCase();
                      return (
                        <div key={`sd-${p.id}`} className="showdown-player">
                          <div className="sd-header">
                            <strong className="sd-pos">{p.position.toUpperCase()}</strong>
                            {isHero && <span className="sd-hero-badge">HERO</span>}
                            <button
                              type="button"
                              className={`sd-toggle sd-winner ${isWinner ? 'active' : ''}`}
                              onClick={()=>{
                                setShowdownWinners(prev=>{ const next=new Set(prev); if(isWinner) next.delete(key); else next.add(key); return next;});
                              }}
                              title="Mark winner (toggle)"
                            >
                              {isWinner ? 'Winner' : 'Mark Winner'}
                            </button>
                          </div>
                          <div className="sd-cards">
                            <CardPickerDialog 
                              cardPosition={`sd-${key}-card1`}
                              currentCard={isHero ? handNote.heroCards.substring(0,2) || first : first}
                              activePickerPos={activePickerPos}
                              setActivePickerPos={setActivePickerPos}
                              onCardSelect={(card)=>{
                                const secondPart = (showdownCardsByPlayer[key]||'').substring(2,4);
                                setShowdownCardsByPlayer(prev=>({ ...prev, [key]: card + secondPart }));
                              }}
                            />
                            <CardPickerDialog 
                              cardPosition={`sd-${key}-card2`}
                              currentCard={isHero ? handNote.heroCards.substring(2,4) || second : second}
                              activePickerPos={activePickerPos}
                              setActivePickerPos={setActivePickerPos}
                              onCardSelect={(card)=>{
                                const firstPart = (showdownCardsByPlayer[key]||'').substring(0,2);
                                setShowdownCardsByPlayer(prev=>({ ...prev, [key]: firstPart + card }));
                              }}
                            />
                            <button
                              type="button"
                              className={`sd-toggle sd-muck ${mucked ? 'active' : ''}`}
                              title="Toggle Muck"
                              onClick={()=> setShowdownMuckByPlayer(prev=>({ ...prev, [key]: !prev[key] }))}
                            >
                              {mucked ? 'Mucked' : 'Muck'}
                            </button>
                          </div>
                          {/* Hand rank display (reuse hero evaluator) */}
                          <div className="sd-rank">
                            {(() => {
                              const boardArr: string[] = [];
                              if (handNote.flopCards.length >= 6) {
                                boardArr.push(handNote.flopCards.substring(0,2), handNote.flopCards.substring(2,4), handNote.flopCards.substring(4,6));
                              }
                              if (handNote.turnCard.length===2) boardArr.push(handNote.turnCard);
                              if (handNote.riverCard.length===2) boardArr.push(handNote.riverCard);
                              const hole: string[] = [];
                              if (!mucked) {
                                if (first.length===2) hole.push(first);
                                if (second.length===2) hole.push(second);
                              }
                              if (hole.length===2) {
                                const ev = evaluateTexasHoldemHand(hole, boardArr);
                                return ev ? `${ev.description}` : '';
                              }
                              return mucked ? 'Mucked' : '';
                            })()}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* No submit/send buttons on showdown; handled by header submit */}
                </div>
              </Tabs.Content>
            </div>
          </Tabs.Root>
          {/* Session info under the table to maximize header space */}
          <div className="session-info-display" style={{marginTop: 6}}>
            <span>{sessionInfoText()}</span>
          </div>
          {/* Timeline Dialog */}
          <Dialog.Root open={showTimeline} onOpenChange={setShowTimeline}>
            <Dialog.Portal>
              <Dialog.Overlay className="card-dialog-overlay" />
              <Dialog.Content className="card-dialog-content" style={{ maxWidth: 720, width: '95%' }}>
                <Dialog.Title className="card-dialog-title">Action Timeline</Dialog.Title>
                {(() => {
                  const TIMELINE_STREETS: StreetTab[] = ['preflop', 'flop', 'turn', 'river'];
                  const orderedPlayers = sortPlayersClockwise(players);
                  const labelFor = (a: Action) => {
                    const type = a.action ? a.action.charAt(0).toUpperCase() + a.action.slice(1) : '';
                    const amount = typeof a.amount === 'number' && a.amount > 0 ? ` ${a.amount}` : '';
                    return `${type}${amount}`;
                  };
                  const styles = {
                    streetsTimeline: {
                      display: 'grid',
                      gridTemplateColumns: 'auto repeat(4, 1fr)',
                      gap: '8px',
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid #1f2937',
                      borderRadius: '10px',
                      padding: '12px',
                      overflowX: 'hidden',
                      marginTop: '6px',
                      color: '#f3f4f6'
                    } as React.CSSProperties,
                    streetHeader: {
                      fontSize: '14px',
                      fontWeight: 700,
                      textAlign: 'center',
                      padding: '6px',
                      textTransform: 'uppercase',
                      borderBottom: '1px solid #1f2937',
                      color: '#e5e7eb'
                    } as React.CSSProperties,
                    playerLabel: {
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      gap: '2px',
                      padding: '6px 6px',
                      color: '#e5e7eb',
                      fontWeight: 700,
                      fontSize: '0.9em',
                      textAlign: 'right',
                      borderRight: '1px solid #1f2937'
                    } as React.CSSProperties,
                    stackSize: {
                      fontSize: '12px',
                      fontWeight: 500,
                      color: '#9ca3af'
                    } as React.CSSProperties,
                    streetCell: {
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                      padding: '8px',
                      background: 'rgba(255, 255, 255, 0.04)',
                      border: '1px solid #1f2937',
                      borderRadius: '6px',
                      minHeight: '52px',
                      transition: 'all 0.2s'
                    } as React.CSSProperties,
                    timelineDots: {
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '6px 12px',
                      alignItems: 'center',
                      padding: '2px 0',
                      position: 'relative',
                      minHeight: '18px'
                    } as React.CSSProperties,
                    timelineDot: {
                      position: 'relative',
                      width: '12px',
                      height: '12px',
                      backgroundColor: '#9ca3af',
                      borderRadius: '50%'
                    } as React.CSSProperties,
                    visibleActionLabel: {
                      position: 'absolute',
                      bottom: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      marginBottom: '6px',
                      backgroundColor: 'rgba(17,24,39,0.95)',
                      color: '#f3f4f6',
                      padding: '4px 8px',
                      borderRadius: '6px',
                      border: '1px solid #1f2937',
                      fontSize: '0.8rem',
                      lineHeight: 1.2,
                      whiteSpace: 'nowrap',
                      zIndex: 10
                    } as React.CSSProperties,
                  };
                  return (
                    <Tabs.Root defaultValue={activeTab} className="tabs-root">
                      <Tabs.List className="tabs-list">
                        {TIMELINE_STREETS.map(street => (
                          <Tabs.Trigger key={`tr-${street}`} value={street} className="tabs-trigger">
                            {street.charAt(0).toUpperCase() + street.slice(1)}
                          </Tabs.Trigger>
                        ))}
                      </Tabs.List>
                      {TIMELINE_STREETS.map(street => (
                        <Tabs.Content key={`content-${street}`} value={street} className="tabs-content">
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {(() => {
                              const toLower = (s: string) => (s || '').toLowerCase();
                              const filteredPlayers = orderedPlayers.filter(p => {
                                const acts = (handNote.actions || []).filter(a => (a.street as StreetTab) === street && toLower(a.playerName) === toLower(p.position));
                                if (street === 'preflop') {
                                  if (acts.length === 0) return false;
                                  const hasNonFold = acts.some(a => toLower(a.action) !== 'fold');
                                  return hasNonFold;
                                }
                                return acts.length > 0;
                              });
                              if (filteredPlayers.length === 0) {
                                return <div style={{ color: '#9ca3af', fontSize: '0.9em' }}>No actions on this street.</div>;
                              }
                              // --- Order rows by acting order ---
                              let streetOrderPositions: string[] = [];
                              if (street === 'preflop') {
                                const preOrder = ['sb','bb', ...PREFLOP_ORDER.filter(p => p !== 'sb' && p !== 'bb')];
                                streetOrderPositions = preOrder;
                              } else {
                                const clockwise = sortPlayersClockwise(players);
                                const firstId = getFirstToActPostflop(players);
                                if (firstId !== null) {
                                  const startIdx = clockwise.findIndex(pl => pl.id === firstId);
                                  const rotated = startIdx >= 0 ? [...clockwise.slice(startIdx), ...clockwise.slice(0, startIdx)] : clockwise;
                                  streetOrderPositions = rotated.map(pl => toLower(pl.position));
                                } else {
                                  streetOrderPositions = sortPlayersClockwise(players).map(pl => toLower(pl.position));
                                }
                              }
                              const rowPlayers = [...filteredPlayers].sort((a,b)=>{
                                const ai = streetOrderPositions.indexOf(toLower(a.position));
                                const bi = streetOrderPositions.indexOf(toLower(b.position));
                                return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
                              });
                              return rowPlayers.map(p => {
                                const acts = (handNote.actions || []).filter(a => (a.street as StreetTab) === street && toLower(a.playerName) === toLower(p.position));
                                return (
                                  <div key={`row-${street}-${p.id}`} style={{ borderBottom: '1px dashed #1f2937', padding: '6px 2px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#e5e7eb', fontWeight: 600 }}>
                                      <span>{p.position.toUpperCase()}</span>
                                      <span style={{ color: '#9ca3af', fontWeight: 500 }}>Stack: {p.stackSize}</span>
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 10px', alignItems: 'center', marginTop: 6 }}>
                                      {acts.map((a, idx) => (
                                        <div key={`act-${street}-${p.id}-${idx}`} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                          <div style={styles.timelineDot} />
                                          <span style={{ ...styles.visibleActionLabel, position: 'static', transform: 'none', marginLeft: 6 }}>
                                            {labelFor(a)}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        </Tabs.Content>
                      ))}
                    </Tabs.Root>
                  );
                })()}
                <Dialog.Close asChild>
                  <button className="card-dialog-close" aria-label="Close"><BsX/></button>
                </Dialog.Close>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
          <RaiseAmountPopup
            isOpen={isRaisePopupOpen}
            onClose={() => setIsRaisePopupOpen(false)}
            onRaise={handleRaiseSubmit}
            bigBlind={parseFloat(handNote.bigBlind)}
            potSize={potSize}
            toCall={(()=>{
              const activePlayer = players.find(p=>p.id===activePlayerId);
              const bbAmt = parseFloat(handNote.bigBlind) || 0;
              const effectiveBet = activeTab === 'preflop' ? (currentBet > 0 ? currentBet : bbAmt) : currentBet;
              const playerContribution = activePlayer ? (lastActions[activePlayer.id] || []).reduce((sum, act) => sum + (act.amount || 0), 0) : 0;
              const need = effectiveBet - playerContribution;
              return need > 0 ? need : 0;
            })()}
            maxAmount={players.find(p=>p.id===activePlayerId)?.stackSize || 0}
            />
          <StackSizePopup
            isOpen={isStackPopupOpen}
            onClose={()=>setIsStackPopupOpen(false)}
            onSave={(val)=>{
              setPlayers(prev=>prev.map(p=>p.id===activePlayerId?{...p,stackSize:val}:p));
            }}
            initialValue={players.find(p=>p.id===activePlayerId)?.stackSize}
            bigBlind={parseFloat(handNote.bigBlind)}
          />
        </form>
      </div>
    </div>
  );
};

export default HandNotesPage; 